/*
NAME: CliWorkdetails
DESC: Componente resposavel por exibir um trabalho da lista de trabalhos concluídos por meio do ID
props:
- item: Array de objetos, de um determinado equipamento
- - - - Default
- style: Object, style object to overwrite this component style

NOTES:
*/

import { Button, IconButton, InputAdornment, Stack, TextField, Typography, FormControl, InputLabel, Select, MenuItem, Checkbox, FormControlLabel, LinearProgress, Box, Autocomplete } from "@mui/material";
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import BasicCard from "../basic/BasicCard";
import RowContainer from "../basic/RowContainer";
import RowContainerItem from "../basic/RowContainerItem";
import values from '../../values/values'
import { useCallback, useEffect, useState, useMemo, useRef } from "react";
import Alert from '@mui/material/Alert';
import DateFormat from "../../usecases/DateFormat";
import strings from "../../values/strings/strings";
import MonitorChartLimits from "../specific/MonitorChartLimits";
import DateRangeIcon from '@mui/icons-material/DateRange';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import SpeedIcon from '@mui/icons-material/Speed';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PersonIcon from '@mui/icons-material/Person';
import MapIcon from '@mui/icons-material/Map';
import ReorderIcon from '@mui/icons-material/Reorder';
import TimelineIcon from '@mui/icons-material/Timeline';
import ThermostatIcon from '@mui/icons-material/Thermostat';
import ScatterPlotIcon from '@mui/icons-material/ScatterPlot';
import InvertColorsIcon from '@mui/icons-material/InvertColors';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import BrokenImageIcon from '@mui/icons-material/BrokenImage';
import RoomIcon from '@mui/icons-material/Room';
import TextureIcon from '@mui/icons-material/Texture';
import LocalDrinkIcon from '@mui/icons-material/LocalDrink';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import PrintIcon from '@mui/icons-material/Print';
import DoneIcon from '@mui/icons-material/Done';
import ViewHeadlineIcon from '@mui/icons-material/ViewHeadline';
import ExpandIcon from '@mui/icons-material/Expand';
import ReportIcon from '@mui/icons-material/Report';
import VerticalAlignTopIcon from '@mui/icons-material/VerticalAlignTop';
import WorkGetErrorVelocidade from "../../usecases/WorkGetErrorVelocidade";
import WorkGetErrorTemperature from "../../usecases/WorkGetErrorTemperature";
import WorkGetErrorSeedsMeter from "../../usecases/WorkGetErrorSeedsMeter";
import WorkGetErrotCurrentPressure from "../../usecases/WorkGetErrotCurrentPressure";
import WorksPulvSectionDisplay from "../specific/WorksPulvSectionDisplay";
import WorksGetData from "../../usecases/WorksGetData";
import ErrorCard from "../basic/ErrorCard";
import MapPanel from "../basic/MapPanel";
import AuthBuildHeaderUserObject from "../../usecases/AuthBuildHeaderUserObject";
import APIManager from "../../connection/APIManager";
import { useHistory } from "react-router-dom/cjs/react-router-dom";
import Reports from "../../usecases/Reports";
import ReportsGraph from "../../usecases/ReportsGraphs";
import ReportsMaps from "../../usecases/ReportsMaps";
import MapPrintHeader, { MAP_PRINT_PAGE_STYLE } from "../specific/MapPrintHeader";
import { collectErrosUnicosFromCoords, getErroTrabalhoLabel } from "../../usecases/ErroTrabalhoLabels";
import { buildMultiWorkErroLinhas, getCenterFromLinhas } from "../../usecases/BuildErroTrabalhoLinhas";
import { getWorkColorByIndex } from "../../usecases/graphMultiWorkUtils";
import { resolveDisplayLineCount, resolveDisplayLineSpacingCm, resolveImplementWidthMeters, resolveReportMapConfigFromTrabalho } from "../../usecases/mapPathUtils";
import { buildTrabalhoPrintInfo, buildTrabalhosResumoInfo } from "../../usecases/buildTrabalhoPrintInfo";
import { useReactToPrint } from "react-to-print";
import styles from './Workdetails.module.css';

const safeToFixed = (value, digits, fallback = ' - - ') =>
  (value != null && !isNaN(Number(value))) ? Number(value).toFixed(digits) : fallback;

const WorkCardTitle = ({ label, workId, color }) => (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <span>{label}</span>
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
            <Box
                sx={{
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    backgroundColor: color || '#19c119',
                    flexShrink: 0,
                    border: '1px solid rgba(0,0,0,0.18)',
                }}
            />
            <Typography component="span" variant="body2" sx={{ fontWeight: 700, color: 'inherit' }}>
                #{workId}
            </Typography>
        </Box>
    </Box>
);

const formatHorasTrabalhadasFromTrabalho = (trab) => {
    let milliseconds = null;
    if (trab?.plantio?.plantio_ponto?.millsec_trabalhados != null) {
        milliseconds = trab.plantio.plantio_ponto.millsec_trabalhados;
    }
    if (trab?.pulv?.pulv_ponto?.millsec_trabalhados != null) {
        milliseconds = trab.pulv.pulv_ponto.millsec_trabalhados;
    }
    if (trab?.gps?.gps_ponto) {
        const ms = trab.gps.gps_ponto.millsec_trabalhados;
        if (ms != null && Number(ms) > 0) {
            milliseconds = ms;
        } else if (trab.data_inicio) {
            const fim = trab.data_fim || trab.gps.gps_ponto.data_cad_device;
            if (fim) {
                const ini = new Date(trab.data_inicio).getTime();
                const f = new Date(fim).getTime();
                if (!isNaN(ini) && !isNaN(f)) milliseconds = Math.max(0, f - ini);
            }
        }
    }
    if (milliseconds == null) return ' - - ';
    const horas = Number(milliseconds) / 1000 / 60 / 60;
    const minutos = (horas - Math.floor(horas)) * 60;
    return `${Math.floor(horas)}h${Math.floor(minutos)}min`;
};

const formatDistanciaFromTrabalho = (trab, coords) => {
    if (coords && Array.isArray(coords) && coords.length >= 2) {
        const validCoords = coords
            .map((item) => {
                const lat = item.c_lat != null ? item.c_lat : (item.lat != null ? item.lat : null);
                const lng = item.c_lng != null ? item.c_lng : (item.lng != null ? item.lng : null);
                const alt = item.c_alt != null ? item.c_alt : (item.alt != null ? item.alt : null);
                if (lat != null && lng != null) {
                    return {
                        lat: parseFloat(lat),
                        lng: parseFloat(lng),
                        alt: alt != null ? parseFloat(alt) : undefined,
                    };
                }
                return null;
            })
            .filter((item) => item !== null);

        if (validCoords.length >= 2) {
            let totalDistance = 0;
            for (let i = 1; i < validCoords.length; i++) {
                const prev = validCoords[i - 1];
                const curr = validCoords[i];
                const distance = parseFloat(values.distanceBetweenpointsM(
                    prev.lat, prev.lng, prev.alt, curr.lat, curr.lng, curr.alt
                ));
                if (typeof distance === 'number' && !isNaN(distance) && isFinite(distance) && distance >= 0) {
                    totalDistance += distance;
                }
            }
            if (typeof totalDistance === 'number' && !isNaN(totalDistance) && isFinite(totalDistance) && totalDistance > 0) {
                return `${totalDistance.toFixed(2)} m`;
            }
        }
    }

    if (trab?.plantio?.plantio_ponto?.metros_percorridos != null) {
        return `${safeToFixed(trab.plantio.plantio_ponto.metros_percorridos, 2, '0.00')} m`;
    }
    if (trab?.pulv?.pulv_ponto?.metros_percorridos != null) {
        return `${safeToFixed(trab.pulv.pulv_ponto.metros_percorridos, 2, '0.00')} m`;
    }
    if (trab?.gps?.gps_ponto?.metros_percorridos != null) {
        return `${safeToFixed(trab.gps.gps_ponto.metros_percorridos, 2, '0.00')} m`;
    }
    return '0.00 m';
};

const buildTrabalhoCardInfo = (trab, histCoords = null, histLinhas = null) => {
    if (!trab) return null;

    let linhasPlantadeira = ' - - ';
    if (trab.plantio?.config_qtd_linhas != null) {
        linhasPlantadeira = trab.plantio.config_qtd_linhas;
    } else if (trab.pulv?.config_qtd_linhas != null) {
        linhasPlantadeira = trab.pulv.config_qtd_linhas;
    } else if (trab.plantio?.plantio_ponto?.linhas) {
        linhasPlantadeira = trab.plantio.plantio_ponto.linhas.length;
    } else if (trab.pulv?.pulv_ponto?.sessoes && Array.isArray(trab.pulv.pulv_ponto.sessoes)) {
        linhasPlantadeira = trab.pulv.pulv_ponto.sessoes.length;
    }

    const configuredLineCount = trab.plantio?.config_qtd_linhas ?? trab.pulv?.config_qtd_linhas;
    const displayLineCount = resolveDisplayLineCount(configuredLineCount, histLinhas);
    if (displayLineCount != null) {
        linhasPlantadeira = displayLineCount;
    }

    let especamentoLinhas = ' - - ';
    if (trab.plantio?.config_espacamento_cm != null) {
        especamentoLinhas = trab.plantio.config_espacamento_cm;
    } else if (trab.pulv?.config_espacamento_cm != null) {
        especamentoLinhas = trab.pulv.config_espacamento_cm;
    }
    const configEspacamentoCm = trab.plantio?.config_espacamento_cm ?? trab.pulv?.config_espacamento_cm;
    if (configEspacamentoCm != null) {
        const displaySpacing = resolveDisplayLineSpacingCm(configEspacamentoCm, configuredLineCount, histLinhas);
        especamentoLinhas = displaySpacing != null ? displaySpacing : configEspacamentoCm;
    }

    const hasPlantio = !!(trab.plantio && trab.plantio.plantio_ponto);
    const hasPulv = !!(trab.pulv && trab.pulv.pulv_ponto);

    return {
        id: trab.id,
        hasPlantio,
        hasPulv,
        dataInicial: trab.data_inicio ? DateFormat(new Date(trab.data_inicio)) : ' - - ',
        dataFinal: trab.data_fim ? DateFormat(new Date(trab.data_fim)) : '- -',
        horasTrabalhadas: formatHorasTrabalhadasFromTrabalho(trab),
        distanciaPercorrida: formatDistanciaFromTrabalho(trab, histCoords),
        operador: trab.operador || ' - - ',
        talhao: trab.talhao || ' - - ',
        linhasPlantadeira,
        antenaAlturaM: trab.antena_altura_m || ' - - ',
        especamentoLinhas,
        velocidadeMaxPermitida: trab.plantio ? (trab.plantio.config_vel_kmh_max ?? ' - - ') : ' - - ',
        areaPlantada: hasPlantio ? `${safeToFixed(trab.plantio.plantio_ponto.areaplantada_ha, 2, '0,00')} ha` : null,
        sementesMetro: hasPlantio ? `${safeToFixed(trab.plantio.plantio_ponto.sementes_metro_total, 2)} s/m` : null,
        erroSementeMetro: hasPlantio ? WorkGetErrorSeedsMeter(trab) : false,
        sementesHectare: hasPlantio ? `${safeToFixed(trab.plantio.plantio_ponto.sementes_hectare_total, 2)} s/ha` : null,
        populacaoTotal: hasPlantio ? `${safeToFixed(trab.plantio.plantio_ponto.populacao, 2)} ppl` : null,
        populacaoMedia: hasPlantio ? `${trab.plantio.plantio_ponto.populacao_media_linha} ppl/ha` : null,
        taxaSementesMetroMax: trab.plantio?.config_sementesmetro_max ?? ' - - ',
        taxaSementesMetro: trab.plantio?.config_sementesmetro ?? ' - - ',
        taxaSementesMetroMin: trab.plantio?.config_sementesmetro_min ?? ' - - ',
        areaPulverizada: hasPulv ? `${safeToFixed(trab.pulv.pulv_ponto.area_pulverizada, 2)} ha` : null,
        vazaoAlvo: hasPulv ? (trab.pulv.pulv_ponto.vazao_geral_lha ?? '----') : '----',
        litrosConsumidos: hasPulv ? `${safeToFixed(trab.pulv.pulv_ponto.litros_consumidos, 2)} L` : null,
        pressaoMaxPsi: trab.pulv?.config_pressao_max_psi ?? ' - - ',
        pressaoMinPsi: trab.pulv?.config_pressao_min_psi ?? ' - - ',
        configTemperaturaMax: trab.pulv?.config_temp_max ?? ' - - ',
    };
};

let plantioRequestCount = 0;
let pulvRequestCount = 0;
let gpsRequestCount = 0;
let plantioLastIdRepeatCount = 0;
let plantioLastRepeatedId = null;
let pulvLastIdRepeatCount = 0;
let pulvLastRepeatedId = null;
let gpsLastIdRepeatCount = 0;
let gpsLastRepeatedId = null;
let linhasRequestCount = 0;
let linhasLastIdRepeatCount = 0;
let linhasLastRepeatedId = null;

function CliWorkdetails({ item, setItemSelecionado }) {
    const navigation = useHistory();
    const [updater, setUpdater] = useState(null)
    const [loading, setloading] = useState(null)
    const [trabalho, settrabalho] = useState(null)  //#TODO - Chamar esta variavel no lugar de item
    const [horasTrabalhadas, sethorasTrabalhadas] = useState(null)
    const [distanciaPercorrida, setdistanciaPercorrida] = useState(null)
    const [operador, setoperador] = useState(null)
    const [talhao, settalhao] = useState(null)
    const [velocidadeMedia, setvelocidadeMedia] = useState("- -")
    const [areaPlantada, setareaPlantada] = useState(null)
    const [sementesMetro, setsementesMetro] = useState(null)
    const [sementesHectare, setsementesHectare] = useState(null)
    const [populacaoTotal, setpopulacaoTotal] = useState(null)
    const [populacaoMedia, setpopulacaoMedia] = useState(null)
    const [linhasPlantadeiraMedia, setlinhasPlantadeira] = useState(null)
    const [areaPulverizada, setareaPulverizada] = useState(null)
    const [pressaoAtual, setpressaoAtual] = useState(null)
    const [vazaoAlvo, setvazaoAlvo] = useState(null)
    const [vazaoAtual, setvazaoAtual] = useState(null)
    const [vazaoAtualMin, setvazaoAtualMin] = useState(null)
    const [litrosConsumidos, setlitrosConsumidos] = useState(null)
    const [temperaturaAtual, settemperaturaAtual] = useState(null)
    const [populacaoMedialinha, setpopulacaoMedialinha] = useState(null)
    const [configVelMax, setconfigVelMax] = useState(null)
    const [configTemperaturaMax, setconfigTemperaturaMax] = useState(0)
    const [pressaoMinPsi, setpressaoMinPsi] = useState(0)
    const [pressaoMaxPsi, setpressaoMaxPsi] = useState(0)
    const [taxaSementesMetroMax, settaxaSementesMetroMax] = useState(0)
    const [taxaSementesMetroMin, settaxaSementesMetroMin] = useState(0)
    const [taxaSementesMetro, settaxaSementesMetro] = useState(0)
    const [antemaAlturaM, setantemaAlturaM] = useState(0)
    const [especamentoLinhas, setespecamentoLinhas] = useState(null)
    const [velocidadeMaxPermitida, setvelocidadeMaxPermitida] = useState(0)
    const [erroTemperaturaAtual, seterroTemperaturaAtual] = useState(false)
    const [validaVelocidade, setvalidaVelocidade] = useState(false)
    const [erroSementeMetro, seterroSementeMetro] = useState(false)
    const [erroPressaoAtual, seterroPressaoAtual] = useState(false)
    const [diftime, setdiftime] = useState(null)
    const [marker, setMarker] = useState([])
    const [linhas, setLinhas] = useState([])
    const [centerMap, setcenterMap] = useState(null)
    const [isLoaded, setIsLoaded] = useState(false)
    const [histLinhas, setHistLinhas] = useState([])
    const [histLinhasInfo, setHistLinhasInfo] = useState(null);
    const [histPulv, setHistPulv] = useState([])
    const [histPlantio, setHistPlantio] = useState([])
    const [histCoords, setHistCoords] = useState(null);
    const [selectedErroTrabalho, setSelectedErroTrabalho] = useState('');
    const [histPlantioInfo, setHistPlantioInfo] = useState(null);
    const [histPulvInfo, setHistPulvInfo] = useState(null);
    const [histCoordsInfo, setHistCoordsInfo] = useState(null);
    const [histGpsInfo, setHistGpsInfo] = useState(null);
    const [histGps, setHistGps] = useState([]);
    const [geralLinhasInfo, setGeralLinhasInfo] = useState([]);
    const [dataLoadingProgress, setDataLoadingProgress] = useState(0);
    const [allDataLoaded, setAllDataLoaded] = useState(false);
    const [allDataPercentageSum, setAllDataPercentageSum] = useState(0);
    
    const [isLoadingPlantio, setIsLoadingPlantio] = useState(false);
    const [isLoadingPulv, setIsLoadingPulv] = useState(false);
    const [isLoadingGps, setIsLoadingGps] = useState(false);
    const [isLoadingLinhas, setIsLoadingLinhas] = useState(false);
    const [dataLoadingStarted, setDataLoadingStarted] = useState(false);

    const [mainVisible, setMainVisible] = useState(true);
    const [availableTrabalhos, setAvailableTrabalhos] = useState([]);
    const [extraTrabalhos, setExtraTrabalhos] = useState([]);
    const [extraSlots, setExtraSlots] = useState([]);
    const nextSlotIdRef = useRef(1);
    const printErroMapRef = useRef(null);
    const [isPrintingErroMap, setIsPrintingErroMap] = useState(false);

    const selectedErroLabel = useMemo(
        () => (selectedErroTrabalho ? getErroTrabalhoLabel(selectedErroTrabalho) : ''),
        [selectedErroTrabalho]
    );

    const handlePrintErroMap = useReactToPrint({
        contentRef: printErroMapRef,
        documentTitle: () => selectedErroLabel || 'Mapa de erro',
        pageStyle: MAP_PRINT_PAGE_STYLE,
        onBeforePrint: async () => {
            setIsPrintingErroMap(true);
            await new Promise((resolve) => setTimeout(resolve, 150));
        },
        onAfterPrint: () => {
            setIsPrintingErroMap(false);
        },
    });

    const printTrabalhosInfo = useMemo(() => {
        const list = [];

        if (mainVisible && trabalho) {
            const mainInfo = buildTrabalhoPrintInfo(trabalho, histCoords);
            if (mainInfo) {
                // Prefere horas/distância já calculadas na tela quando disponíveis
                list.push({
                    ...mainInfo,
                    horasTrabalhadas: horasTrabalhadas || mainInfo.horasTrabalhadas,
                    distanciaPercorrida: distanciaPercorrida || mainInfo.distanciaPercorrida,
                    operador: operador || mainInfo.operador,
                    talhao: talhao || mainInfo.talhao,
                });
            }
        }

        for (const extra of extraTrabalhos) {
            if (!extra?.trabalho) {
                continue;
            }
            const info = buildTrabalhoPrintInfo(extra.trabalho, extra.histCoords);
            if (info) {
                list.push(info);
            }
        }

        return list;
    }, [
        mainVisible,
        trabalho,
        histCoords,
        horasTrabalhadas,
        distanciaPercorrida,
        operador,
        talhao,
        extraTrabalhos,
    ]);

    const trabalhosResumoInfo = useMemo(() => {
        const entries = [];

        if (trabalho) {
            entries.push({ trabalho, histCoords });
        }

        for (const extra of extraTrabalhos) {
            if (!extra?.trabalho) {
                continue;
            }
            entries.push({
                trabalho: extra.trabalho,
                histCoords: extra.histCoords,
            });
        }

        return buildTrabalhosResumoInfo(entries);
    }, [trabalho, histCoords, extraTrabalhos]);

    const zoomMap = 15;
    const limitRows = 2000;

    const workColorById = useMemo(() => {
        const ids = [];
        if (item?.id) ids.push(item.id);
        extraSlots.forEach((slot) => {
            if (slot.trabalhoId && !ids.includes(slot.trabalhoId)) {
                ids.push(slot.trabalhoId);
            }
        });
        const map = {};
        ids.forEach((id, index) => {
            map[id] = id === item?.id ? '#19c119' : getWorkColorByIndex(index - 1);
        });
        return map;
    }, [item, extraSlots]);

    const erroMapWorks = useMemo(() => {
        const works = [];
        const openedId = trabalho?.id ?? item?.id;

        if (mainVisible && Array.isArray(histCoords) && histCoords.length > 0) {
            works.push({
                id: openedId,
                histCoords,
                color: workColorById[openedId] || '#19c119',
            });
        }

        for (const extra of extraTrabalhos) {
            if (!Array.isArray(extra?.histCoords) || extra.histCoords.length === 0) {
                continue;
            }
            works.push({
                id: extra.id,
                histCoords: extra.histCoords,
                color: workColorById[extra.id] || getWorkColorByIndex(works.length),
            });
        }

        return works;
    }, [mainVisible, histCoords, trabalho, item, extraTrabalhos, workColorById]);

    const hasErroMapCoords = erroMapWorks.length > 0;

    const trabalhoErros = useMemo(() => {
        const codigos = new Set();
        for (const work of erroMapWorks) {
            collectErrosUnicosFromCoords(work.histCoords).forEach((codigo) => codigos.add(codigo));
        }
        return Array.from(codigos).sort((a, b) =>
            getErroTrabalhoLabel(a).localeCompare(getErroTrabalhoLabel(b), 'pt-BR')
        );
    }, [erroMapWorks]);

    useEffect(() => {
        if (selectedErroTrabalho && !trabalhoErros.includes(selectedErroTrabalho)) {
            setSelectedErroTrabalho('');
        }
    }, [trabalhoErros, selectedErroTrabalho]);

    const linhasErroTrabalhoData = useMemo(
        () => buildMultiWorkErroLinhas(erroMapWorks, selectedErroTrabalho),
        [erroMapWorks, selectedErroTrabalho]
    );

    const centerMapErroTrabalho = useMemo(
        () => getCenterFromLinhas(
            linhasErroTrabalhoData.linhas,
            centerMap,
            linhasErroTrabalhoData.savedPoints
        ),
        [linhasErroTrabalhoData, centerMap]
    );

    const implementWidthMeters = useMemo(
        () => resolveImplementWidthMeters(trabalho),
        [trabalho]
    );

    const mainReportMapConfig = useMemo(
        () => resolveReportMapConfigFromTrabalho(trabalho),
        [trabalho]
    );

    useEffect(() => {
        if(item) {
            plantioRequestCount = 0;
            pulvRequestCount = 0;
            gpsRequestCount = 0;
            linhasRequestCount = 0;
            linhasLastIdRepeatCount = 0;
            linhasLastRepeatedId = null;
            setIsLoaded(false);
            setAllDataLoaded(false);
            setDataLoadingProgress(0);
            setAllDataPercentageSum(0);
            setIsLoadingPlantio(false);
            setIsLoadingPulv(false);
            setIsLoadingGps(false);
            setIsLoadingLinhas(false);
            setHistLinhas([]);
            setHistLinhasInfo(null);
            setDataLoadingStarted(false);
            
            WorksGetData(item.id, false).then(trabalho => {
                settrabalho(trabalho[0]);
            }).catch(err => {
                console.error('Erro ao buscar trabalho:', err);
                settrabalho(null);
            }).finally(() => {
                setIsLoaded(true);
            });
        }else {
            settrabalho(null)
        }
    }, [])

    useEffect(() => {
        if (trabalho && !dataLoadingStarted) {
            setDataLoadingStarted(true);
            bindData(trabalho);
            getHistCoords(trabalho);
            getLastPonto(trabalho);
            getDataInfo(trabalho);
        }
      }, [trabalho, dataLoadingStarted]);

    // Mesma verificação do mapa de sementes/metro: se o histórico indicar
    // plantio alternado (ou não houver config), o input usa a qtd de linhas do histórico
    // e dobra o espaçamento entre linhas.
    useEffect(() => {
        if (!trabalho || isLoadingLinhas) {
            return;
        }
        if (!histLinhasInfo && (!histLinhas || histLinhas.length === 0)) {
            return;
        }

        const configuredLineCount =
            trabalho.plantio?.config_qtd_linhas ?? trabalho.pulv?.config_qtd_linhas;
        const displayLineCount = resolveDisplayLineCount(configuredLineCount, histLinhas);

        if (displayLineCount != null) {
            setlinhasPlantadeira(displayLineCount);
        }

        const configEspacamentoCm =
            trabalho.plantio?.config_espacamento_cm ?? trabalho.pulv?.config_espacamento_cm;
        if (configEspacamentoCm != null) {
            const displaySpacing = resolveDisplayLineSpacingCm(
                configEspacamentoCm,
                configuredLineCount,
                histLinhas
            );
            setespecamentoLinhas(displaySpacing != null ? displaySpacing : configEspacamentoCm);
        }
    }, [trabalho, histLinhas, histLinhasInfo, isLoadingLinhas]);

    useEffect(() => {
        const fetchAvailableTrabalhos = async () => {
            try {
                const trabalhosList = await WorksGetData(null, false);
                if (Array.isArray(trabalhosList)) {
                    const filtered = trabalhosList.filter(t => t.id !== item?.id);
                    setAvailableTrabalhos(filtered);
                }
            } catch (err) {
                console.error('Erro ao buscar lista de trabalhos disponíveis:', err);
                setAvailableTrabalhos([]);
            }
        };
        if (item) fetchAvailableTrabalhos();
    }, [item]);

    const formatTrabalhoLabel = useCallback((t) => {
        if (!t) return '';
        const parts = [`#${t.id}`];
        if (t.data_inicio) parts.push(DateFormat(new Date(t.data_inicio)));
        if (t.cultura && t.cultura.nome) parts.push(t.cultura.nome);
        if (t.talhao) parts.push(t.talhao);
        if (t.operador) parts.push(t.operador);
        const tipos = [];
        if (t.plantio?.id) tipos.push('Plantio');
        if (t.pulv?.id) tipos.push('Pulverização');
        parts.push(tipos.length > 0 ? tipos.join(' e ') : 'GPS');
        return parts.join(' | ');
    }, []);

    const loadExtraTrabalhoData = useCallback(async (trabalhoId) => {
        const entry = {
            id: trabalhoId,
            trabalho: null,
            histCoords: [],
            histPlantio: [],
            histPulv: [],
            histGps: [],
            histLinhas: [],
            loading: true,
            progress: 0,
        };

        setExtraTrabalhos(prev => [...prev, entry]);

        try {
            const trabalhoArr = await WorksGetData(trabalhoId, false);
            const trab = Array.isArray(trabalhoArr) ? trabalhoArr[0] : trabalhoArr;
            if (!trab) {
                setExtraTrabalhos(prev => prev.map(e => e.id === trabalhoId ? { ...e, loading: false, progress: 100 } : e));
                return;
            }

            setExtraTrabalhos(prev => prev.map(e => e.id === trabalhoId ? { ...e, trabalho: trab, progress: 5 } : e));

            const obj = await AuthBuildHeaderUserObject();

            const infoEndpoints = [
                { key: 'plantio', url: '/trabalho/histplantio/info' },
                { key: 'pulv', url: '/trabalho/histpulv/info' },
                { key: 'coords', url: '/trabalho/histcoords/info' },
                { key: 'gps', url: '/trabalho/histgps/info' },
                { key: 'linhas', url: '/trabalho/histlinhas/info' },
            ];

            const infos = {};
            for (const ep of infoEndpoints) {
                try {
                    const res = await APIManager.send(ep.url, 'GET', { ...obj, trabalho_id: trabalhoId });
                    infos[ep.key] = await res.json();
                } catch (e) { infos[ep.key] = { rows: 0 }; }
            }

            const totalRows = (infos.plantio?.rows || 0) + (infos.pulv?.rows || 0) +
                              (infos.coords?.rows || 0) + (infos.gps?.rows || 0) + (infos.linhas?.rows || 0);
            let loadedRows = 0;

            const updateProgress = (newRows) => {
                loadedRows += newRows;
                const pct = totalRows > 0 ? Math.min(Math.round((loadedRows / totalRows) * 100), 100) : 100;
                setExtraTrabalhos(prev => prev.map(e => e.id === trabalhoId ? { ...e, progress: pct } : e));
            };

            const MAX_REQUESTS_PER_ENDPOINT = 200;

            const fetchPaginated = async (url, infoData) => {
                if (!infoData || infoData.rows === 0) return [];
                let all = [];
                let iniDate = infoData.first_date || null;
                let lastId = null;
                let repeats = 0;
                let lastRepeatedId = null;
                let requestCount = 0;

                while (all.length < infoData.rows && requestCount < MAX_REQUESTS_PER_ENDPOINT) {
                    requestCount++;
                    try {
                        const headers = { ...obj, trabalho_id: trabalhoId, limit_rows: limitRows, ini_date: iniDate };
                        if (lastId) headers.last_id = lastId;

                        const res = await APIManager.send(url, 'GET', headers);
                        const batch = await res.json();

                        if (!batch || batch.length === 0) break;

                        const existingIds = new Set(all.map(r => r.id));
                        const newRecords = batch.filter(r => !existingIds.has(r.id));
                        if (newRecords.length === 0) break;

                        all = [...all, ...newRecords];
                        updateProgress(newRecords.length);

                        const lastRecord = all[all.length - 1];
                        const newLastId = lastRecord.id;
                        iniDate = lastRecord.data || lastRecord.date;

                        if (newLastId === lastId) {
                            if (lastRepeatedId === newLastId) repeats++;
                            else { lastRepeatedId = newLastId; repeats = 1; }
                            if (repeats >= 3) break;
                        } else {
                            repeats = 0;
                            lastRepeatedId = null;
                        }
                        lastId = newLastId;

                        if (all.length >= infoData.rows * 0.95) break;
                    } catch (e) {
                        console.error(`Erro na paginação de ${url} para trabalho ${trabalhoId}:`, e);
                        break;
                    }
                }
                return all;
            };

            const dataSteps = [
                { url: '/trabalho/histcoords', infoKey: 'coords', stateKey: 'histCoords' },
                { url: '/trabalho/histplantio', infoKey: 'plantio', stateKey: 'histPlantio' },
                { url: '/trabalho/histpulv', infoKey: 'pulv', stateKey: 'histPulv' },
                { url: '/trabalho/histgps', infoKey: 'gps', stateKey: 'histGps' },
                { url: '/trabalho/histlinhas', infoKey: 'linhas', stateKey: 'histLinhas' },
            ];

            for (const step of dataSteps) {
                const result = await fetchPaginated(step.url, infos[step.infoKey]);
                setExtraTrabalhos(prev => prev.map(e =>
                    e.id === trabalhoId ? { ...e, [step.stateKey]: result } : e
                ));
            }

            setExtraTrabalhos(prev => prev.map(e => e.id === trabalhoId ? { ...e, loading: false, progress: 100 } : e));
        } catch (err) {
            console.error(`Erro ao carregar dados do trabalho extra ${trabalhoId}:`, err);
            setExtraTrabalhos(prev => prev.map(e => e.id === trabalhoId ? { ...e, loading: false, progress: 100 } : e));
        }
    }, []);

    const removeExtraTrabalho = useCallback((slotId) => {
        setExtraSlots(prev => {
            const slot = prev.find(s => s.slotId === slotId);
            if (slot && slot.trabalhoId) {
                setExtraTrabalhos(prevExtra => prevExtra.filter(e => e.id !== slot.trabalhoId));
            }
            return prev.filter(s => s.slotId !== slotId);
        });
    }, []);

    const addExtraSlot = useCallback(() => {
        const slotId = nextSlotIdRef.current++;
        setExtraSlots(prev => [...prev, { slotId, trabalhoId: null }]);
    }, []);

    const selectExtraTrabalho = useCallback((slotId, trabalhoId) => {
        setExtraSlots(prev => {
            const slot = prev.find(s => s.slotId === slotId);
            if (slot && slot.trabalhoId) {
                setExtraTrabalhos(prevExtra => prevExtra.filter(e => e.id !== slot.trabalhoId));
            }
            return prev.map(s => s.slotId === slotId ? { ...s, trabalhoId } : s);
        });
        if (trabalhoId && !extraTrabalhos.some(e => e.id === trabalhoId)) {
            loadExtraTrabalhoData(trabalhoId);
        }
    }, [loadExtraTrabalhoData, extraTrabalhos]);

    const selectedExtraIds = useMemo(() => {
        return new Set(extraSlots.map(s => s.trabalhoId).filter(Boolean));
    }, [extraSlots]);

    const selectedWorksCardInfo = useMemo(() => {
        const entries = [];
        if (trabalho) {
            entries.push({
                trabalho,
                histCoords,
                histLinhas,
                color: workColorById[trabalho.id] || workColorById[item?.id],
            });
        }
        extraSlots.forEach((slot) => {
            if (!slot.trabalhoId) return;
            const extra = extraTrabalhos.find((e) => e.id === slot.trabalhoId);
            if (!extra?.trabalho) return;
            entries.push({
                trabalho: extra.trabalho,
                histCoords: extra.histCoords,
                histLinhas: extra.histLinhas,
                color: workColorById[slot.trabalhoId],
            });
        });
        return entries
            .map((entry) => {
                const info = buildTrabalhoCardInfo(entry.trabalho, entry.histCoords, entry.histLinhas);
                if (!info) return null;
                return { ...info, color: entry.color };
            })
            .filter(Boolean);
    }, [trabalho, histCoords, histLinhas, extraSlots, extraTrabalhos, workColorById, item]);

    const mergedReportData = useMemo(() => {
        const works = [];

        const buildWorkEntry = (hist, mapConfig, meta = {}) => {
            const entry = [
                { histLinhas: hist.histLinhas || [], histLinhasReady: hist.histLinhasReady === true },
                { histPulv: hist.histPulv || [] },
                { histPlantio: hist.histPlantio || [] },
                { histCoords: hist.histCoords || [] },
                { histGps: hist.histGps || [] },
            ];
            entry.mapConfig = mapConfig || {};
            entry.trabalhoId = meta.id ?? null;
            entry.label = meta.label || (meta.id != null ? `#${meta.id}` : 'Trabalho');
            entry.color = meta.color || workColorById[meta.id] || (meta.id === item?.id ? '#19c119' : undefined);
            return entry;
        };

        if (mainVisible) {
            works.push(buildWorkEntry(
                {
                    histLinhas: histLinhas || [],
                    histLinhasReady: !isLoadingLinhas && histLinhasInfo !== null,
                    histPulv: histPulv || [],
                    histPlantio: histPlantio || [],
                    histCoords: histCoords || [],
                    histGps: histGps || [],
                },
                resolveReportMapConfigFromTrabalho(trabalho),
                {
                    id: trabalho?.id ?? item?.id,
                    label: `#${trabalho?.id ?? item?.id ?? ''}`,
                }
            ));
        }

        for (const extra of extraTrabalhos) {
            works.push(buildWorkEntry(
                {
                    histLinhas: extra.histLinhas || [],
                    histLinhasReady: !extra.loading,
                    histPulv: extra.histPulv || [],
                    histPlantio: extra.histPlantio || [],
                    histCoords: extra.histCoords || [],
                    histGps: extra.histGps || [],
                },
                resolveReportMapConfigFromTrabalho(extra.trabalho),
                {
                    id: extra.id,
                    label: `#${extra.id}`,
                }
            ));
        }

        let allLinhas = [];
        let allPulv = [];
        let allPlantio = [];
        let allCoords = [];
        let allGps = [];
        let linhasReady = true;

        for (const work of works) {
            if (work[0]?.histLinhas?.length) allLinhas = [...allLinhas, ...work[0].histLinhas];
            if (work[1]?.histPulv?.length) allPulv = [...allPulv, ...work[1].histPulv];
            if (work[2]?.histPlantio?.length) allPlantio = [...allPlantio, ...work[2].histPlantio];
            if (work[3]?.histCoords?.length) allCoords = [...allCoords, ...work[3].histCoords];
            if (work[4]?.histGps?.length) allGps = [...allGps, ...work[4].histGps];
            if (work[0]?.histLinhasReady !== true) linhasReady = false;
        }

        const merged = [
            { histLinhas: allLinhas, histLinhasReady: linhasReady },
            { histPulv: allPulv },
            { histPlantio: allPlantio },
            { histCoords: allCoords },
            { histGps: allGps },
        ];
        merged.works = works;
        return merged;
    }, [mainVisible, trabalho, item, histLinhas, histPulv, histPlantio, histCoords, histGps, isLoadingLinhas, histLinhasInfo, extraTrabalhos, workColorById]);

    const mergedLinhas = useMemo(() => {
        const worksCoords = [];
        if (mainVisible && histCoords?.length) worksCoords.push(histCoords);
        for (const extra of extraTrabalhos) {
            if (extra.histCoords?.length) worksCoords.push(extra.histCoords);
        }

        if (!worksCoords.length) return { linhas: [], markers: [], center: centerMap };

        const segments = [];
        let firstCoord = null;
        let lastCoord = null;

        for (const workCoords of worksCoords) {
            const coords = workCoords
                .filter(c => c.lat != null && c.lng != null)
                .map(c => ({ lat: parseFloat(c.lat), lng: parseFloat(c.lng), alt: c.alt != null ? parseFloat(c.alt) : undefined }));

            if (!coords.length) continue;

            if (!firstCoord) firstCoord = coords[0];
            lastCoord = coords[coords.length - 1];

            let seg = [{ lat: coords[0].lat, lng: coords[0].lng }];
            for (let i = 1; i < coords.length; i++) {
                const prev = coords[i - 1];
                const curr = coords[i];
                const dist = values.distanceBetweenpointsM(prev.lat, prev.lng, prev.alt, curr.lat, curr.lng, curr.alt);
                if (dist > 20 && seg.length > 0) {
                    segments.push({ path: seg });
                    seg = [];
                }
                seg.push({ lat: curr.lat, lng: curr.lng });
            }
            if (seg.length > 0) segments.push({ path: seg });
        }

        if (!firstCoord || !lastCoord) return { linhas: [], markers: [], center: centerMap };

        const markers = [
            { latitude: firstCoord.lat, longitude: firstCoord.lng, coordinateType: 'first' },
            { latitude: lastCoord.lat, longitude: lastCoord.lng, coordinateType: 'last' },
        ];

        return { linhas: segments, markers, center: { lat: lastCoord.lat, lng: lastCoord.lng } };
    }, [mainVisible, histCoords, extraTrabalhos, centerMap]);

    const updateDataPercentage = (valueIncrease) => {
        setAllDataPercentageSum(prevSum => {
            const newSum = prevSum + valueIncrease;
            
            const totalExpected = (histPlantioInfo && histPlantioInfo.rows ? histPlantioInfo.rows : 0) +
                                 (histPulvInfo && histPulvInfo.rows ? histPulvInfo.rows : 0) +
                                 (histCoordsInfo && histCoordsInfo.rows ? histCoordsInfo.rows : 0) +
                                 (histGpsInfo && histGpsInfo.rows ? histGpsInfo.rows : 0);
            
            const percentage = totalExpected > 0 
                ? Math.min(parseInt((newSum * 100) / totalExpected), 100)
                : 0;
    
            setDataLoadingProgress(percentage);
            
            return newSum;
        });
    };
    
    useEffect(() => {
        if (!trabalho) {
            setAllDataLoaded(false);
            return;
        }
        
        const timeoutId = setTimeout(() => {
            const hasInfo = histPlantioInfo && !Array.isArray(histPlantioInfo) && histPlantioInfo.rows !== undefined &&
                           histPulvInfo && !Array.isArray(histPulvInfo) && histPulvInfo.rows !== undefined &&
                           histCoordsInfo && !Array.isArray(histCoordsInfo) && histCoordsInfo.rows !== undefined &&
                           histGpsInfo && !Array.isArray(histGpsInfo) && histGpsInfo.rows !== undefined;
            
            if (hasInfo) {
                const plantioRows = histPlantioInfo.rows || 0;
                const pulvRows = histPulvInfo.rows || 0;
                const coordsRows = histCoordsInfo.rows || 0;
                const gpsRows = histGpsInfo.rows || 0;
                const totalExpected = plantioRows + pulvRows + coordsRows + gpsRows;
                
                let loadedCount = 0;
                if (plantioRows === 0) {
                    loadedCount += 0;
                } else if (histPlantio && Array.isArray(histPlantio)) {
                    loadedCount += Math.min(histPlantio.length, plantioRows);
                }
                
                if (pulvRows === 0) {
                    loadedCount += 0;
                } else if (histPulv && Array.isArray(histPulv)) {
                    loadedCount += Math.min(histPulv.length, pulvRows);
                }
                
                if (coordsRows === 0) {
                    loadedCount += 0;
                } else if (histCoords !== null && histCoords !== undefined && Array.isArray(histCoords)) {
                    loadedCount += Math.min(histCoords.length, coordsRows);
                }
                
                if (gpsRows === 0) {
                    loadedCount += 0;
                } else if (histGps && Array.isArray(histGps)) {
                    loadedCount += Math.min(histGps.length, gpsRows);
                }
                
                const percentage = totalExpected > 0 
                    ? Math.min(parseInt((loadedCount * 100) / totalExpected), 100)
                    : 100;
                
                setDataLoadingProgress(percentage);
                
                const plantioDone = (plantioRows === 0) || (histPlantio && Array.isArray(histPlantio) && histPlantio.length >= plantioRows);
                const pulvDone = (pulvRows === 0) || (histPulv && Array.isArray(histPulv) && histPulv.length >= pulvRows);
                const coordsDone = (coordsRows === 0) || (histCoords !== null && histCoords !== undefined && Array.isArray(histCoords) && histCoords.length >= coordsRows);
                const gpsDone = (gpsRows === 0) || (histGps && Array.isArray(histGps) && histGps.length >= gpsRows);
                
                if (plantioDone && pulvDone && coordsDone && gpsDone) {
                    setAllDataLoaded(true);
                    setDataLoadingProgress(100);
                }
            } else {
                setDataLoadingProgress(5);
            }
        }, 300);
        
        return () => clearTimeout(timeoutId);
    }, [trabalho, histPlantio, histPulv, histCoords, histGps, histPlantioInfo, histPulvInfo, histCoordsInfo, histGpsInfo, isLoadingPlantio, isLoadingPulv, isLoadingGps]);
    // const updateDataPercentage = (valueIncrease) => {
    //     setTotalDataPercentage(prevPercentage => {
    //         const newPercentage = prevPercentage + valueIncrease;
    //         return newPercentage > 100 ? 100 : newPercentage;
    //     });
    // };
    

    const getDataInfo = async (trabalho) => {
        const obj = await AuthBuildHeaderUserObject();
        try {
            // HistPlantioInfo
            const histPlantioHeaders = {
                ...obj,
                trabalho_id: trabalho.id,
            };
            const histPlantioResponse = await APIManager.send('/trabalho/histplantio/info', 'GET', histPlantioHeaders);
            const histPlantioJson = await histPlantioResponse.json();
            setHistPlantioInfo(histPlantioJson);

            // HistPulvInfo
            const histPulvHeaders = {
                ...obj,
                trabalho_id: trabalho.id,
            };
            const histPulvResponse = await APIManager.send('/trabalho/histpulv/info', 'GET', histPulvHeaders);
            const histPulvJson = await histPulvResponse.json();
            setHistPulvInfo(histPulvJson);

            // HistCoordsInfo
            const histCoordsHeaders = {
                ...obj,
                trabalho_id: trabalho.id,
            };
            const histCoordsResponse = await APIManager.send('/trabalho/histcoords/info', 'GET', histCoordsHeaders);
            const histCoordsJson = await histCoordsResponse.json();
            setHistCoordsInfo(histCoordsJson);

            // HistGpsInfo
            const histGpsHeaders = {
                ...obj,
                trabalho_id: trabalho.id,
            };
            const histGpsResponse = await APIManager.send('/trabalho/histgps/info', 'GET', histGpsHeaders);
            const histGpsJson = await histGpsResponse.json();
            setHistGpsInfo(histGpsJson);

            // GeralLinhasInfo
            const geralLinhasHeaders = {
                ...obj,
                trabalho_id: trabalho.id,
                numero: 1,
            };
            const geralLinhasResponse = await APIManager.send('/trabalho/geraldatalinhas', 'GET', geralLinhasHeaders);
            const geralLinhasJson = await geralLinhasResponse.json();
            setGeralLinhasInfo(geralLinhasJson);

            const histLinhasHeaders = {
                ...obj,
                trabalho_id: trabalho.id,
            };
            const histLinhasInfoResponse = await APIManager.send('/trabalho/histlinhas/info', 'GET', histLinhasHeaders);
            const histLinhasInfoJson = await histLinhasInfoResponse.json();
            setHistLinhasInfo(histLinhasInfoJson);

            updateDataPercentage(12.5);
            
            if (histPlantioJson && histPlantioJson.rows === 0) {
                setHistPlantio([]);
            } else if (histPlantioJson && histPlantioJson.rows > 0) {
                await getHistPlantio(trabalho, [], histPlantioJson.first_date, histPlantioJson.rows);
            } else {
                setHistPlantio([]);
            }
            
            if (histPulvJson && histPulvJson.rows === 0) {
                setHistPulv([]);
            } else if (histPulvJson && histPulvJson.rows > 0) {
                await getHistPulv(trabalho, [], histPulvJson.first_date, histPulvJson.rows);
            } else {
                setHistPulv([]);
            }
            
            if (histCoordsJson && histCoordsJson.rows === 0) {
                setHistCoords([]);
            }

            if (histGpsJson && histGpsJson.rows === 0) {
                setHistGps([]);
            } else if (histGpsJson && histGpsJson.rows > 0) {
                await getHistGps(trabalho, [], histGpsJson.first_date, histGpsJson.rows);
            } else {
                setHistGps([]);
            }

            if (histLinhasInfoJson && histLinhasInfoJson.rows === 0) {
                setHistLinhas([]);
            } else if (histLinhasInfoJson && histLinhasInfoJson.rows > 0) {
                await getHistLinhas(trabalho, [], histLinhasInfoJson.first_date, histLinhasInfoJson.rows);
            } else {
                setHistLinhas([]);
            }
            
        } catch (error) {
            console.error('Erro ao carregar informações sobre o serviço:', error);
            setHistLinhasInfo({ rows: 0 });
            setHistLinhas([]);
            setIsLoadingLinhas(false);
        }
    };
    
    const getLastPonto = async (trabalho) => {
        try {
            const obj = await AuthBuildHeaderUserObject();
            const headers = {
                ...obj,
                trabalho_id: trabalho.id,
            };

            const response = await APIManager.send('/trabalho/lastponto', 'GET', headers);
            const json = await response.json();
            updateDataPercentage(12.5);
        } catch (error) {
            console.error('Erro ao carregar dados de lastPonto:', error);
        }
    }

    const getHistPlantio = async (trabalho, historic = [], iniDate = null, totalRowsExpected = null, lastId = null) => {
        if (historic.length === 0) {
            if (isLoadingPlantio) {
                return;
            }
            setIsLoadingPlantio(true);
            plantioLastIdRepeatCount = 0;
            plantioLastRepeatedId = null;
        }
        
        const trabalhoId = trabalho.id;
        const totalRows = totalRowsExpected || (histPlantioInfo && histPlantioInfo.rows ? histPlantioInfo.rows : null);
    
        try {
            plantioRequestCount++;
            
            let lastIdToSend = lastId;
            
            if (historic.length > 0) {
                if (lastId === null && plantioRequestCount === 1) {
                    lastIdToSend = historic[historic.length - 1].id;
                    historic.pop();
                } else {
                    lastIdToSend = historic[historic.length - 1].id;
                }
            }
            
            const previousDate = iniDate;
            
            const obj = await AuthBuildHeaderUserObject();
            const headers = {
                ...obj,
                trabalho_id: trabalhoId,
                limit_rows: limitRows,
                ini_date: iniDate,
            };
            
            if (lastIdToSend) {
                headers.last_id = lastIdToSend;
            }
    
            const response = await APIManager.send('/trabalho/histplantio', 'GET', headers);
            const newHistplantio = await response.json();
            
            if (!newHistplantio || newHistplantio.length === 0) {
                if (historic.length < totalRows * 0.95) {
                    if (lastIdToSend !== null) {
                        const obj2 = await AuthBuildHeaderUserObject();
                        const headers2 = {
                            ...obj2,
                            trabalho_id: trabalhoId,
                            limit_rows: limitRows,
                            last_id: lastIdToSend,
                        };
                        
                        const response2 = await APIManager.send('/trabalho/histplantio', 'GET', headers2);
                        const newHistplantio2 = await response2.json();
                        
                        if (newHistplantio2 && newHistplantio2.length > 0) {
                            const existingIds = new Set(historic.map(h => h.id));
                            const newRecords = newHistplantio2.filter(r => !existingIds.has(r.id));
                            
                            if (newRecords.length > 0) {
                                newRecords.sort((a, b) => new Date(a.data) - new Date(b.data));
                                const finalHistoric = [...historic, ...newRecords].sort((a, b) => new Date(a.data) - new Date(b.data) || a.id - b.id);
                                
                                if (finalHistoric.length < totalRows * 0.95) {
                                    const newLastId = finalHistoric[finalHistoric.length - 1].id;
                                    const newDate = finalHistoric[finalHistoric.length - 1].data;
                                    await getHistPlantio(trabalho, finalHistoric, newDate, totalRows, newLastId);
                                    return;
                                } else {
                                    setHistPlantio(finalHistoric);
                                    setIsLoadingPlantio(false);
                                    return;
                                }
                            }
                        }
                    }
                    
                    if (historic.length < totalRows * 0.8) {
                        const obj3 = await AuthBuildHeaderUserObject();
                        const headers3 = {
                            ...obj3,
                            trabalho_id: trabalhoId,
                            limit_rows: totalRows,
                        };
                        
                        const response3 = await APIManager.send('/trabalho/histplantio', 'GET', headers3);
                        const allHistplantio = await response3.json();
                        
                        if (allHistplantio && allHistplantio.length > 0) {
                            const existingIds = new Set(historic.map(h => h.id));
                            const newRecords = allHistplantio.filter(r => !existingIds.has(r.id));
                            
                            if (newRecords.length > 0) {
                                const finalHistoric = [...historic, ...newRecords].sort((a, b) => new Date(a.data) - new Date(b.data) || a.id - b.id);
                                setHistPlantio(finalHistoric);
                                setIsLoadingPlantio(false);
                                return;
                            }
                        }
                    }
                }
                
                setHistPlantio(historic.length > 0 ? historic : []);
                setIsLoadingPlantio(false);
                return;
            }

            let updatedHistoric = [...historic, ...newHistplantio];
            
            const newDate = updatedHistoric[updatedHistoric.length - 1].data;
            const newLastId = updatedHistoric[updatedHistoric.length - 1].id;
            
            if (updatedHistoric.length >= totalRows) {
                setHistPlantio(updatedHistoric);
                setIsLoadingPlantio(false);
                return;
            }
            
            if (lastIdToSend !== null && newLastId === lastIdToSend) {
                if (plantioLastRepeatedId === newLastId) {
                    plantioLastIdRepeatCount++;
                } else {
                    plantioLastRepeatedId = newLastId;
                    plantioLastIdRepeatCount = 1;
                }
                
                if (plantioLastIdRepeatCount >= 3) {
                    setHistPlantio(updatedHistoric);
                    setIsLoadingPlantio(false);
                    return;
                }
            } else {
                plantioLastIdRepeatCount = 0;
                plantioLastRepeatedId = null;
            }
            
            if (newHistplantio.length <= 2 && lastIdToSend !== null && newLastId === lastIdToSend && updatedHistoric.length >= totalRows * 0.9) {
                setHistPlantio(updatedHistoric);
                setIsLoadingPlantio(false);
                return;
            }
            
            if (newHistplantio.length <= 2 && previousDate === newDate && updatedHistoric.length >= totalRows * 0.9) {
                setHistPlantio(updatedHistoric);
                setIsLoadingPlantio(false);
                return;
            }

            if (totalRows && totalRows > 0) {
                updateDataPercentage(newHistplantio.length);
            }
            
            await getHistPlantio(trabalho, updatedHistoric, newDate, totalRows, newLastId);
            
        } catch (error) {
            if (historic && historic.length > 0) {
                setHistPlantio(historic);
            }
            setIsLoadingPlantio(false);
        }
    };
    

    const getHistLinhas = async (trabalho, historic = [], iniDate = null, totalRowsExpected = null, lastId = null) => {
        if (historic.length === 0) {
            if (isLoadingLinhas) {
                return;
            }
            setIsLoadingLinhas(true);
            linhasLastIdRepeatCount = 0;
            linhasLastRepeatedId = null;
        }

        const totalRows = totalRowsExpected || (histLinhasInfo && histLinhasInfo.rows ? histLinhasInfo.rows : null);

        try {
            linhasRequestCount++;

            let lastIdToSend = lastId;

            if (historic.length > 0) {
                if (lastId === null && linhasRequestCount === 1) {
                    lastIdToSend = historic[historic.length - 1].id;
                    historic.pop();
                } else {
                    lastIdToSend = historic[historic.length - 1].id;
                }
            }

            const previousDate = iniDate;

            const obj = await AuthBuildHeaderUserObject();
            const headers = {
                ...obj,
                trabalho_id: trabalho.id,
                limit_rows: limitRows,
                ini_date: iniDate,
            };

            if (lastIdToSend) {
                headers.last_id = lastIdToSend;
            }

            const response = await APIManager.send('/trabalho/histlinhas', 'GET', headers);
            const newHistLinhas = await response.json();

            if (!newHistLinhas || newHistLinhas.length === 0) {
                setHistLinhas(historic.length > 0 ? historic : []);
                setIsLoadingLinhas(false);
                return;
            }

            const existingIds = new Set(historic.map((h) => h.id));
            const newRecords = newHistLinhas.filter((r) => !existingIds.has(r.id));
            const updatedHistoric = newRecords.length > 0
                ? [...historic, ...newRecords].sort((a, b) => new Date(a.data) - new Date(b.data) || a.id - b.id)
                : historic;

            if (updatedHistoric.length >= totalRows) {
                setHistLinhas(updatedHistoric);
                setIsLoadingLinhas(false);
                return;
            }

            const newDate = updatedHistoric[updatedHistoric.length - 1].data;
            const newLastId = updatedHistoric[updatedHistoric.length - 1].id;

            if (lastIdToSend !== null && newLastId === lastIdToSend) {
                if (linhasLastRepeatedId === newLastId) {
                    linhasLastIdRepeatCount++;
                } else {
                    linhasLastRepeatedId = newLastId;
                    linhasLastIdRepeatCount = 1;
                }

                if (linhasLastIdRepeatCount >= 3) {
                    setHistLinhas(updatedHistoric);
                    setIsLoadingLinhas(false);
                    return;
                }
            } else {
                linhasLastIdRepeatCount = 0;
                linhasLastRepeatedId = null;
            }

            if (newRecords.length === 0 && newHistLinhas.length > 0) {
                setHistLinhas(updatedHistoric);
                setIsLoadingLinhas(false);
                return;
            }

            if (newHistLinhas.length < limitRows) {
                setHistLinhas(updatedHistoric);
                setIsLoadingLinhas(false);
                return;
            }

            if (newHistLinhas.length <= 2 && previousDate === newDate && updatedHistoric.length >= totalRows * 0.9) {
                setHistLinhas(updatedHistoric);
                setIsLoadingLinhas(false);
                return;
            }

            if (totalRows && totalRows > 0) {
                updateDataPercentage(newRecords.length || newHistLinhas.length);
            }

            await getHistLinhas(trabalho, updatedHistoric, newDate, totalRows, newLastId);
        } catch (error) {
            console.error('Erro ao carregar histórico das linhas:', error);
            if (historic.length > 0) {
                setHistLinhas(historic);
            }
            setIsLoadingLinhas(false);
        }
    };

    const getHistPulv = async (trabalho, historic = [], iniDate = null, totalRowsExpected = null, lastId = null) => {
        if (historic.length === 0) {
            if (isLoadingPulv) {
                return;
            }
            setIsLoadingPulv(true);
            pulvLastIdRepeatCount = 0;
            pulvLastRepeatedId = null;
        }
        
        const trabalhoId = trabalho.id;
        const totalRows = totalRowsExpected || (histPulvInfo && histPulvInfo.rows ? histPulvInfo.rows : null);
    
        try {
            pulvRequestCount++;
            
            let lastIdToSend = lastId;
            
            if (historic.length > 0) {
                if (lastId === null && pulvRequestCount === 1) {
                    lastIdToSend = historic[historic.length - 1].id;
                    historic.pop();
                } else {
                    lastIdToSend = historic[historic.length - 1].id;
                }
            }
            
            const previousDate = iniDate;
            
            const obj = await AuthBuildHeaderUserObject();
            const headers = {
                ...obj,
                trabalho_id: trabalhoId,
                limit_rows: limitRows,
                ini_date: iniDate,
            };
            
            if (lastIdToSend) {
                headers.last_id = lastIdToSend;
            }
    
            const response = await APIManager.send('/trabalho/histpulv', 'GET', headers);
            const newHistpulv = await response.json();
            
            if (!newHistpulv || newHistpulv.length === 0) {
                if (historic.length < totalRows * 0.95) {
                    if (lastIdToSend !== null) {
                        const obj2 = await AuthBuildHeaderUserObject();
                        const headers2 = {
                            ...obj2,
                            trabalho_id: trabalhoId,
                            limit_rows: limitRows,
                            last_id: lastIdToSend,
                        };
                        
                        const response2 = await APIManager.send('/trabalho/histpulv', 'GET', headers2);
                        const newHistpulv2 = await response2.json();
                        
                        if (newHistpulv2 && newHistpulv2.length > 0) {
                            const existingIds = new Set(historic.map(h => h.id));
                            const newRecords = newHistpulv2.filter(r => !existingIds.has(r.id));
                            
                            if (newRecords.length > 0) {
                                newRecords.sort((a, b) => new Date(a.data) - new Date(b.data));
                                const finalHistoric = [...historic, ...newRecords].sort((a, b) => new Date(a.data) - new Date(b.data) || a.id - b.id);
                                
                                if (finalHistoric.length < totalRows * 0.95) {
                                    const newLastId = finalHistoric[finalHistoric.length - 1].id;
                                    const newDate = finalHistoric[finalHistoric.length - 1].data;
                                    await getHistPulv(trabalho, finalHistoric, newDate, totalRows, newLastId);
                                    return;
                                } else {
                                    setHistPulv(finalHistoric);
                                    setIsLoadingPulv(false);
                                    return;
                                }
                            }
                        }
                    }
                    
                    if (historic.length < totalRows * 0.8) {
                        const obj3 = await AuthBuildHeaderUserObject();
                        const headers3 = {
                            ...obj3,
                            trabalho_id: trabalhoId,
                            limit_rows: totalRows,
                        };
                        
                        const response3 = await APIManager.send('/trabalho/histpulv', 'GET', headers3);
                        const allHistpulv = await response3.json();
                        
                        if (allHistpulv && allHistpulv.length > 0) {
                            const existingIds = new Set(historic.map(h => h.id));
                            const newRecords = allHistpulv.filter(r => !existingIds.has(r.id));
                            
                            if (newRecords.length > 0) {
                                const finalHistoric = [...historic, ...newRecords].sort((a, b) => new Date(a.data) - new Date(b.data) || a.id - b.id);
                                setHistPulv(finalHistoric);
                                setIsLoadingPulv(false);
                                return;
                            }
                        }
                    }
                }
                
                setHistPulv(historic.length > 0 ? historic : []);
                setIsLoadingPulv(false);
                return;
            }
    
            let updatedHistoric = [...historic, ...newHistpulv];
            
            const newDate = updatedHistoric[updatedHistoric.length - 1].data;
            const newLastId = updatedHistoric[updatedHistoric.length - 1].id; // ID do último registro para próxima paginação
            
            if (updatedHistoric.length >= totalRows) {
                setHistPulv(updatedHistoric);
                setIsLoadingPulv(false);
                return;
            }
            
            if (lastIdToSend !== null && newLastId === lastIdToSend) {
                if (pulvLastRepeatedId === newLastId) {
                    pulvLastIdRepeatCount++;
                } else {
                    pulvLastRepeatedId = newLastId;
                    pulvLastIdRepeatCount = 1;
                }
                
                if (pulvLastIdRepeatCount >= 3) {
                    setHistPulv(updatedHistoric);
                    setIsLoadingPulv(false);
                    return;
                }
            } else {
                pulvLastIdRepeatCount = 0;
                pulvLastRepeatedId = null;
            }
            
            if (newHistpulv.length <= 2 && lastIdToSend !== null && newLastId === lastIdToSend && updatedHistoric.length >= totalRows * 0.9) {
                setHistPulv(updatedHistoric);
                setIsLoadingPulv(false);
                return;
            }
            
            if (newHistpulv.length <= 2 && previousDate === newDate && updatedHistoric.length >= totalRows * 0.9) {
                setHistPulv(updatedHistoric);
                setIsLoadingPulv(false);
                return;
            }

            if (totalRows && totalRows > 0) {
                updateDataPercentage(newHistpulv.length);
            }
            
            await getHistPulv(trabalho, updatedHistoric, newDate, totalRows, newLastId);
            
        } catch (error) {
            if (historic && historic.length > 0) {
                setHistPulv(historic);
            }
            setIsLoadingPulv(false);
        }
    }

    const getHistGps = async (trabalho, historic = [], iniDate = null, totalRowsExpected = null, lastId = null) => {
        if (historic.length === 0) {
            if (isLoadingGps) {
                return;
            }
            setIsLoadingGps(true);
            gpsLastIdRepeatCount = 0;
            gpsLastRepeatedId = null;
        }
        
        const trabalhoId = trabalho.id;
        const totalRows = totalRowsExpected || (histGpsInfo && histGpsInfo.rows ? histGpsInfo.rows : null);
    
        try {
            gpsRequestCount++;
            
            let lastIdToSend = lastId;
            
            if (historic.length > 0) {
                if (lastId === null && gpsRequestCount === 1) {
                    lastIdToSend = historic[historic.length - 1].id;
                    historic.pop();
                } else {
                    lastIdToSend = historic[historic.length - 1].id;
                }
            }
            
            const previousDate = iniDate;
            
            const obj = await AuthBuildHeaderUserObject();
            const headers = {
                ...obj,
                trabalho_id: trabalhoId,
                limit_rows: limitRows,
                ini_date: iniDate,
            };
            
            if (lastIdToSend) {
                headers.last_id = lastIdToSend;
            }
    
            const response = await APIManager.send('/trabalho/histgps', 'GET', headers);
            const newHistgps = await response.json();
            
            if (!newHistgps || newHistgps.length === 0) {
                if (historic.length < totalRows * 0.95) {
                    if (lastIdToSend !== null) {
                        const obj2 = await AuthBuildHeaderUserObject();
                        const headers2 = {
                            ...obj2,
                            trabalho_id: trabalhoId,
                            limit_rows: limitRows,
                            last_id: lastIdToSend,
                        };
                        
                        const response2 = await APIManager.send('/trabalho/histgps', 'GET', headers2);
                        const newHistgps2 = await response2.json();
                        
                        if (newHistgps2 && newHistgps2.length > 0) {
                            const existingIds = new Set(historic.map(h => h.id));
                            const newRecords = newHistgps2.filter(r => !existingIds.has(r.id));
                            
                            if (newRecords.length > 0) {
                                newRecords.sort((a, b) => new Date(a.data) - new Date(b.data));
                                const finalHistoric = [...historic, ...newRecords].sort((a, b) => new Date(a.data) - new Date(b.data) || a.id - b.id);
                                
                                if (finalHistoric.length < totalRows * 0.95) {
                                    const newLastId = finalHistoric[finalHistoric.length - 1].id;
                                    const newDate = finalHistoric[finalHistoric.length - 1].data;
                                    await getHistGps(trabalho, finalHistoric, newDate, totalRows, newLastId);
                                    return;
                                } else {
                                    setHistGps(finalHistoric);
                                    setIsLoadingGps(false);
                                    return;
                                }
                            }
                        }
                    }
                    
                    if (historic.length < totalRows * 0.8) {
                        const obj3 = await AuthBuildHeaderUserObject();
                        const headers3 = {
                            ...obj3,
                            trabalho_id: trabalhoId,
                            limit_rows: totalRows,
                        };
                        
                        const response3 = await APIManager.send('/trabalho/histgps', 'GET', headers3);
                        const allHistgps = await response3.json();
                        
                        if (allHistgps && allHistgps.length > 0) {
                            const existingIds = new Set(historic.map(h => h.id));
                            const newRecords = allHistgps.filter(r => !existingIds.has(r.id));
                            
                            if (newRecords.length > 0) {
                                const finalHistoric = [...historic, ...newRecords].sort((a, b) => new Date(a.data) - new Date(b.data) || a.id - b.id);
                                setHistGps(finalHistoric);
                                setIsLoadingGps(false);
                                return;
                            }
                        }
                    }
                }
                
                setHistGps(historic.length > 0 ? historic : []);
                setIsLoadingGps(false);
                return;
            }
    
            let updatedHistoric = [...historic, ...newHistgps];
            
            const newDate = updatedHistoric[updatedHistoric.length - 1].data;
            const newLastId = updatedHistoric[updatedHistoric.length - 1].id;
            
            if (updatedHistoric.length >= totalRows) {
                setHistGps(updatedHistoric);
                setIsLoadingGps(false);
                return;
            }
            
            if (lastIdToSend !== null && newLastId === lastIdToSend) {
                if (gpsLastRepeatedId === newLastId) {
                    gpsLastIdRepeatCount++;
                } else {
                    gpsLastRepeatedId = newLastId;
                    gpsLastIdRepeatCount = 1;
                }
                
                if (gpsLastIdRepeatCount >= 3) {
                    setHistGps(updatedHistoric);
                    setIsLoadingGps(false);
                    return;
                }
            } else {
                gpsLastIdRepeatCount = 0;
                gpsLastRepeatedId = null;
            }
            
            if (newHistgps.length <= 2 && lastIdToSend !== null && newLastId === lastIdToSend && updatedHistoric.length >= totalRows * 0.9) {
                setHistGps(updatedHistoric);
                setIsLoadingGps(false);
                return;
            }
            
            if (newHistgps.length <= 2 && previousDate === newDate && updatedHistoric.length >= totalRows * 0.9) {
                setHistGps(updatedHistoric);
                setIsLoadingGps(false);
                return;
            }

            if (totalRows && totalRows > 0) {
                updateDataPercentage(newHistgps.length);
            }
            
            await getHistGps(trabalho, updatedHistoric, newDate, totalRows, newLastId);
            
        } catch (error) {
            if (historic && historic.length > 0) {
                setHistGps(historic);
            }
            setIsLoadingGps(false);
        }
    }

    const getHistCoords = (trabalho) => {
        if (!trabalho || !trabalho.id) {
            console.warn('Trabalho inválido para carregar dados do mapa');
            return;
        }
        
        const trabalhoId = trabalho.id; 
    
        const fetchData = async () => {
            try {
                const obj = await AuthBuildHeaderUserObject();
                const headers = {
                    ...obj,
                    trabalho_id: trabalhoId,
                };
    
                const response = await APIManager.send('/trabalho/histcoords', 'GET', headers);
                
                if (!response.ok) {
                    throw new Error(`Erro na resposta da API: ${response.status}`);
                }
                
                const json = await response.json();
    
    
                if (!json || !Array.isArray(json) || json.length === 0) {
                    console.warn('Resposta da API não contém dados válidos de coordenadas');
                    setMarker([]);
                    setLinhas([]);
                    setHistCoords([]);
                    return;
                }

                setHistCoords(json);
    
                polilinhas(json);
            } catch (error) {
                console.error('Erro ao carregar dados da API:', error);
                setMarker([]);
                setLinhas([]);
                setHistCoords([]);
            }
        };
    
        fetchData();
    };

    const polilinhas = (trabalho) => {
        // Valida se há dados
        if (!trabalho || !Array.isArray(trabalho) || trabalho.length === 0) {
            console.warn('Nenhuma coordenada disponível para exibir no mapa');
            setMarker([]);
            setLinhas([]);
            return;
        }
    
        const coords = trabalho
            .filter(item => item.lat != null && item.lng != null) // Filtra coordenadas válidas
            .map(item => ({
                lat: parseFloat(item.lat),
                lng: parseFloat(item.lng),
                alt: item.alt != null ? parseFloat(item.alt) : undefined,
            }));
        
        if (coords.length === 0) {
            console.warn('Nenhuma coordenada válida encontrada');
            setMarker([]);
            setLinhas([]);
            return;
        }
        
        const linhasSegmentos = [];
        let segmentoAtual = [{ lat: coords[0].lat, lng: coords[0].lng }];
        for (let i = 1; i < coords.length; i++) {
            const prev = coords[i - 1];
            const curr = coords[i];
            const distance = values.distanceBetweenpointsM(
                prev.lat, prev.lng, prev.alt,
                curr.lat, curr.lng, curr.alt
            );
            if (distance > 20 && segmentoAtual.length > 0) {
                linhasSegmentos.push({ path: segmentoAtual });
                segmentoAtual = [];
            }
            segmentoAtual.push({ lat: curr.lat, lng: curr.lng });
        }
        if (segmentoAtual.length > 0) {
            linhasSegmentos.push({ path: segmentoAtual });
        }
        
        const lastPonto = {
            latitude: coords[coords.length - 1].lat,
            longitude: coords[coords.length - 1].lng,
            coordinateType: 'last',
        };
        const firstPonto = {
            latitude: coords[0].lat,
            longitude: coords[0].lng,
            coordinateType: 'first',
        };
        
        const markers = [lastPonto, firstPonto];
        const centerMap = { lat: coords[coords.length - 1].lat, lng: coords[coords.length - 1].lng };
        
        setMarker(markers);
        setLinhas(linhasSegmentos);
        setcenterMap(centerMap);
    }

    const calculateDistanceFromCoords = useCallback((coords) => {
        if (!coords || !Array.isArray(coords) || coords.length < 2) {
            return 0;
        }

        let totalDistance = 0;
        // Normaliza coordenadas: pode vir com c_lat/c_lng/c_alt (estilo API) ou lat/lng/alt
        const validCoords = coords
            .map(item => {
                const lat = item.c_lat != null ? item.c_lat : (item.lat != null ? item.lat : null);
                const lng = item.c_lng != null ? item.c_lng : (item.lng != null ? item.lng : null);
                const alt = item.c_alt != null ? item.c_alt : (item.alt != null ? item.alt : null);
                
                if (lat != null && lng != null) {
                    return {
                        lat: parseFloat(lat),
                        lng: parseFloat(lng),
                        alt: alt != null ? parseFloat(alt) : undefined
                    };
                }
                return null;
            })
            .filter(item => item !== null);
        
        if (validCoords.length < 2) {
            return 0;
        }
        
        for (let i = 1; i < validCoords.length; i++) {
            const prev = validCoords[i - 1];
            const curr = validCoords[i];
            const distanceStr = values.distanceBetweenpointsM(
                prev.lat,
                prev.lng,
                prev.alt,
                curr.lat,
                curr.lng,
                curr.alt
            );
            const distance = parseFloat(distanceStr);
            
            if (typeof distance === 'number' && !isNaN(distance) && isFinite(distance) && distance >= 0) {
                totalDistance += distance;
            }
        }
        
        return typeof totalDistance === 'number' && !isNaN(totalDistance) && isFinite(totalDistance) 
            ? totalDistance 
            : 0;
    }, []);

    const bindData = (trabalho) => {
        //Dados Gerais
        let op = ' - - '
        if (trabalho.operador) { op = trabalho.operador }
        setoperador(op)

        let talh = ' - - '
        if (trabalho.talhao) { talh = trabalho.talhao }
        settalhao(talh)

        let vel = ' - - '
        if (trabalho.coordenada && trabalho.coordenada.velocidade_antena_kmh != null) {
            vel = safeToFixed(trabalho.coordenada.velocidade_antena_kmh, 1, ' - - ') + ' km/h'
        }
        setvelocidadeMedia(vel)

        setvalidaVelocidade(WorkGetErrorVelocidade(trabalho))

        let dist = ' - - '
        if (trabalho.plantio && trabalho.plantio.plantio_ponto && trabalho.plantio.plantio_ponto.metros_percorridos != null) {
            dist = safeToFixed(trabalho.plantio.plantio_ponto.metros_percorridos, 2, '0.00') + ` m`
        } else if (trabalho.pulv && trabalho.pulv.pulv_ponto && trabalho.pulv.pulv_ponto.metros_percorridos != null) {
            dist = safeToFixed(trabalho.pulv.pulv_ponto.metros_percorridos, 2, '0.00') + ` m`
        } else if (trabalho.gps && trabalho.gps.gps_ponto && trabalho.gps.gps_ponto.metros_percorridos != null) {
            dist = safeToFixed(trabalho.gps.gps_ponto.metros_percorridos, 2, '0.00') + ` m`
        } else {
            dist = '0.00 m '
        }
        setdistanciaPercorrida(dist)

        ////Dados de Plantio
        if(trabalho.plantio && trabalho.plantio.plantio_ponto) {
        let areaPlant = ' 0,00 ha'
        if (trabalho.plantio) { areaPlant = safeToFixed(trabalho.plantio.plantio_ponto.areaplantada_ha, 2, '0,00') + ` ha` }
        setareaPlantada(areaPlant)

        let sementesM = ' - - '
        if (trabalho.plantio) { sementesM = safeToFixed(trabalho.plantio.plantio_ponto.sementes_metro_total, 2) + ` s/m` }
        setsementesMetro(sementesM)

        seterroSementeMetro(WorkGetErrorSeedsMeter(trabalho))

        let sementesH = ' - - '
        if (trabalho.plantio) { sementesH = safeToFixed(trabalho.plantio.plantio_ponto.sementes_hectare_total, 2) + ` s/ha` }
        setsementesHectare(sementesH)

        let populacaoT = ' - - '
        if (trabalho.plantio) { populacaoT = safeToFixed(trabalho.plantio.plantio_ponto.populacao, 2) + ` ppl` }
        setpopulacaoTotal(populacaoT)

        let populacaoM = ' - - '
        if (trabalho.plantio) { populacaoM = trabalho.plantio.plantio_ponto.populacao_media_linha + ` ppl/ha` }  //condicional de populacao media
        setpopulacaoMedia(populacaoM)
        }

        // Quantidade de linhas: config do plantio/pulv; fallback legado.
        // Após carregar histLinhas, resolveDisplayLineCount pode sobrescrever
        // (ex.: plantio alternado → usa a qtd de linhas do histórico).
        let linhasPlant = ' - - '
        if (trabalho.plantio?.config_qtd_linhas != null) {
            linhasPlant = trabalho.plantio.config_qtd_linhas
        } else if (trabalho.pulv?.config_qtd_linhas != null) {
            linhasPlant = trabalho.pulv.config_qtd_linhas
        } else if (trabalho.plantio?.plantio_ponto?.linhas) {
            linhasPlant = trabalho.plantio.plantio_ponto.linhas.length
        } else if (trabalho.pulv?.pulv_ponto?.sessoes && Array.isArray(trabalho.pulv.pulv_ponto.sessoes)) {
            linhasPlant = trabalho.pulv.pulv_ponto.sessoes.length
        }
        setlinhasPlantadeira(linhasPlant)

        ////pulverizacao
        if(trabalho.pulv && trabalho.pulv.pulv_ponto) {
        let areaPulv = ' - - '
        if (trabalho.pulv) { areaPulv = safeToFixed(trabalho.pulv.pulv_ponto.area_pulverizada, 2) + ` ha` }
        setareaPulverizada(areaPulv)

        let pressao = ' - - '
        if (trabalho.pulv) { pressao = safeToFixed(trabalho.pulv.pulv_ponto.pressao_atual_psi, 2) + ` psi` }
        setpressaoAtual(pressao)

        seterroPressaoAtual(WorkGetErrotCurrentPressure(trabalho))

        let temperatura = ' - - '
        if (trabalho.pulv) { temperatura = (trabalho.pulv.pulv_ponto.temperatura != null ? trabalho.pulv.pulv_ponto.temperatura : ' - - ') + ` ºC` }
        settemperaturaAtual(temperatura)

        seterroTemperaturaAtual(WorkGetErrorTemperature(trabalho))

        let litro = ' - - '
        if (trabalho.pulv) { litro = safeToFixed(trabalho.pulv.pulv_ponto.litros_consumidos, 2) + ` L` }
        setlitrosConsumidos(litro)
        }

        let maxTemp = ' - - '
        if (trabalho.pulv) { maxTemp = trabalho.pulv.config_temp_max }  //condicional de temperatura maxima
        setconfigTemperaturaMax(maxTemp)

        let PressaoMinPsi = ' - - '
        if (trabalho.pulv) { PressaoMinPsi = trabalho.pulv.config_pressao_min_psi }  //condicional de pressao minima
        setpressaoMinPsi(PressaoMinPsi)

        let PressaoMaxPsi = ' - - '
        if (trabalho.pulv) { PressaoMaxPsi = trabalho.pulv.config_pressao_max_psi }  //condicional de pressao maxima
        setpressaoMaxPsi(PressaoMaxPsi)

        let TaxaSementeMetroMax = ' - - '
        if (trabalho.plantio) { TaxaSementeMetroMax = trabalho.plantio.config_sementesmetro_max }  //condicional de taxa de sementes por metro
        settaxaSementesMetroMax(TaxaSementeMetroMax)

        let TaxaSementeMetroMin = ' - - '
        if (trabalho.plantio) { TaxaSementeMetroMin = trabalho.plantio.config_sementesmetro_min }  //condicional de taxa de sementes por metro
        settaxaSementesMetroMin(TaxaSementeMetroMin)

        let TaxaSementeMetro = ' - - '
        if (trabalho.plantio) { TaxaSementeMetro = trabalho.plantio.config_sementesmetro }  //condicional de taxa de sementes por metro
        settaxaSementesMetro(TaxaSementeMetro)

        let AntenaAlturaM = ' - - '
        if (trabalho.antena_altura_m) { AntenaAlturaM = trabalho.antena_altura_m }  //condicional de taxa de sementes por metro
        setantemaAlturaM(AntenaAlturaM)

        // Espaçamento: config do plantio/pulv; após histLinhas, resolveDisplayLineSpacingCm
        // pode dobrar o valor (plantio alternado).
        let EspacamentoLinhas = ' - - '
        if (trabalho.plantio?.config_espacamento_cm != null) {
            EspacamentoLinhas = trabalho.plantio.config_espacamento_cm
        } else if (trabalho.pulv?.config_espacamento_cm != null) {
            EspacamentoLinhas = trabalho.pulv.config_espacamento_cm
        }
        setespecamentoLinhas(EspacamentoLinhas)

        let VelocidadeMaxPermitida = ' - - '
        if (trabalho.plantio) { VelocidadeMaxPermitida = trabalho.plantio.config_vel_kmh_max }  //condicional de taxa de sementes por metro
        setvelocidadeMaxPermitida(VelocidadeMaxPermitida)
        

        let now = new Date()
        let date = new Date(trabalho.data_update_server)
        let dif = now.getTime() - date.getTime()
        dif = dif / 1000 / 60
        setdiftime(dif)
        
        let milliseconds = null;
        if (trabalho.plantio && trabalho.plantio.plantio_ponto && trabalho.plantio.plantio_ponto.millsec_trabalhados != null) {
            milliseconds = trabalho.plantio.plantio_ponto.millsec_trabalhados;
        }
        if (trabalho.pulv && trabalho.pulv.pulv_ponto && trabalho.pulv.pulv_ponto.millsec_trabalhados != null) {
            milliseconds = trabalho.pulv.pulv_ponto.millsec_trabalhados;
        }
        if (trabalho.gps && trabalho.gps.gps_ponto) {
            const ms = trabalho.gps.gps_ponto.millsec_trabalhados;
            if (ms != null && Number(ms) > 0) milliseconds = ms;
            else if (trabalho.data_inicio) {
                const fim = trabalho.data_fim || trabalho.gps.gps_ponto.data_cad_device;
                if (fim) {
                    const ini = new Date(trabalho.data_inicio).getTime();
                    const f = new Date(fim).getTime();
                    if (!isNaN(ini) && !isNaN(f)) milliseconds = Math.max(0, f - ini);
                }
            }
        }

        if (milliseconds != null) {
            let horas = Number(milliseconds) / 1000 / 60 / 60
            let minutos = horas - Math.floor(horas)
            minutos = minutos * 60
            sethorasTrabalhadas(Math.floor(horas) + 'h' + Math.floor(minutos) + 'min')
        }

        
        //Vazão
        if (trabalho.pulv && trabalho.pulv.pulv_ponto) {
            setvazaoAlvo(trabalho.pulv.pulv_ponto.vazao_geral_lha)
            setvazaoAtual(trabalho.pulv.pulv_ponto.vazao_atual_lha)
            setvazaoAtualMin(trabalho.pulv.pulv_ponto.vazao_atual_lmin)
        } else {
            setvazaoAlvo('----')
            setvazaoAtual('----')
            setvazaoAtualMin('----')
        }

        updateDataPercentage(12.5);
    }

    // Calcula distância percorrida a partir das coordenadas quando disponível
    // Prioriza cálculo a partir das coordenadas, que é mais preciso
    useEffect(() => {
        // Se há coordenadas válidas (array com pelo menos 2 pontos), calcula a distância
        if (histCoords && Array.isArray(histCoords) && histCoords.length >= 2) {
            const distanceFromCoords = calculateDistanceFromCoords(histCoords);
            // Garante que distanceFromCoords é um número válido antes de chamar toFixed
            if (typeof distanceFromCoords === 'number' && !isNaN(distanceFromCoords) && isFinite(distanceFromCoords) && distanceFromCoords > 0) {
                setdistanciaPercorrida(distanceFromCoords.toFixed(2) + ` m`);
            } else {
                // Fallback: plantio → pulverização → gps
                if (trabalho?.plantio?.plantio_ponto?.metros_percorridos != null) {
                    setdistanciaPercorrida(safeToFixed(trabalho.plantio.plantio_ponto.metros_percorridos, 2, '0.00') + ` m`);
                } else if (trabalho?.pulv?.pulv_ponto?.metros_percorridos != null) {
                    setdistanciaPercorrida(safeToFixed(trabalho.pulv.pulv_ponto.metros_percorridos, 2, '0.00') + ` m`);
                } else if (trabalho?.gps?.gps_ponto?.metros_percorridos != null) {
                    setdistanciaPercorrida(safeToFixed(trabalho.gps.gps_ponto.metros_percorridos, 2, '0.00') + ` m`);
                } else {
                    setdistanciaPercorrida('0.00 m');
                }
            }
        } 
        // Se coordenadas foram carregadas mas não há dados suficientes, usa fallback
        else if (histCoords !== null && Array.isArray(histCoords) && histCoords.length < 2 && trabalho) {
            // Fallback: plantio → pulverização → gps
            if (trabalho.plantio && trabalho.plantio.plantio_ponto && trabalho.plantio.plantio_ponto.metros_percorridos != null) {
                setdistanciaPercorrida(safeToFixed(trabalho.plantio.plantio_ponto.metros_percorridos, 2, '0.00') + ` m`);
            } else if (trabalho.pulv && trabalho.pulv.pulv_ponto && trabalho.pulv.pulv_ponto.metros_percorridos != null) {
                setdistanciaPercorrida(safeToFixed(trabalho.pulv.pulv_ponto.metros_percorridos, 2, '0.00') + ` m`);
            } else if (trabalho.gps && trabalho.gps.gps_ponto && trabalho.gps.gps_ponto.metros_percorridos != null) {
                setdistanciaPercorrida(safeToFixed(trabalho.gps.gps_ponto.metros_percorridos, 2, '0.00') + ` m`);
            } else {
                setdistanciaPercorrida('0.00 m');
            }
        }
        else if (histCoords === null && trabalho) {
            if (trabalho.plantio && trabalho.plantio.plantio_ponto && trabalho.plantio.plantio_ponto.metros_percorridos != null) {
                setdistanciaPercorrida(safeToFixed(trabalho.plantio.plantio_ponto.metros_percorridos, 2, '0.00') + ` m`);
            } else if (trabalho.pulv && trabalho.pulv.pulv_ponto && trabalho.pulv.pulv_ponto.metros_percorridos != null) {
                setdistanciaPercorrida(safeToFixed(trabalho.pulv.pulv_ponto.metros_percorridos, 2, '0.00') + ` m`);
            } else if (trabalho.gps && trabalho.gps.gps_ponto && trabalho.gps.gps_ponto.metros_percorridos != null) {
                setdistanciaPercorrida(safeToFixed(trabalho.gps.gps_ponto.metros_percorridos, 2, '0.00') + ` m`);
            }
        }
    }, [trabalho, histCoords, calculateDistanceFromCoords]);

    const onGoBack = useCallback(() => {
        const id = window.location.pathname.split('/')[2]
        navigation.replace("/works", "/" + id)
        setItemSelecionado(null) 
    }, [setItemSelecionado, navigation]);

    return (
        <>
            <Button onClick={onGoBack}>Voltar</Button>
            <div style={{ flex: 1, display: 'flex', width: '100%', maxWidth: '100%', minWidth: 0, boxSizing: 'border-box' }}>
                {item && !trabalho && (
                    <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100vh', width: '100%' }}>
                        <p>Carregando dados do trabalho...</p>
                    </div>
                )}
                {trabalho && (
                    <RowContainer style={{ marginBottom: values.margin[1], }}>
                        <RowContainerItem style={{ marginBottom: values.margin[1], }}>
                                <BasicCard style={{ marginBottom: values.margin[1], flexDirection: 'column' }}>
                                    <Typography variant="subtitle1" color="primary" sx={{ mb: 1, fontWeight: 'bold', width: '100%' }}>
                                        <ReorderIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                                        Trabalhos selecionados
                                    </Typography>

                                    {/* Trabalho atual */}
                                    <Box sx={{ width: '100%', mb: 1, p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1, backgroundColor: 'action.hover' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
                                                <Box sx={{ width: 16, height: 16, borderRadius: '50%', backgroundColor: workColorById[item?.id] || workColorById[trabalho?.id] || '#19c119', flexShrink: 0 }} />
                                                <Typography variant="body2" sx={{ fontWeight: 'bold', flex: 1 }}>
                                                    {trabalho ? formatTrabalhoLabel(trabalho) : `#${item?.id || ''}`}
                                                    <Typography component="span" variant="caption" color="primary" sx={{ ml: 1 }}>(aberto)</Typography>
                                                </Typography>
                                            </Box>
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={mainVisible}
                                                        onChange={(e) => setMainVisible(e.target.checked)}
                                                        size="small"
                                                    />
                                                }
                                                label={<Typography variant="caption">Visualizar</Typography>}
                                                sx={{ ml: 1, mr: 0, flexShrink: 0 }}
                                            />
                                        </Box>
                                        <LinearProgress
                                            variant="determinate"
                                            value={dataLoadingProgress}
                                            sx={{ mt: 0.5, height: 4, borderRadius: 2 }}
                                        />
                                        {!allDataLoaded && (
                                            <Typography variant="caption" color="text.secondary">
                                                Carregando dados... {dataLoadingProgress}%
                                            </Typography>
                                        )}
                                    </Box>

                                    {/* Trabalhos extras */}
                                    {extraSlots.map((slot) => {
                                        const extraData = extraTrabalhos.find(e => e.id === slot.trabalhoId);
                                        const selectableTrabalhos = availableTrabalhos.filter(t =>
                                            t.id !== item?.id && (!selectedExtraIds.has(t.id) || t.id === slot.trabalhoId)
                                        );
                                        return (
                                            <Box key={slot.slotId} sx={{ width: '100%', mb: 1, p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    {slot.trabalhoId && (
                                                        <Box sx={{ width: 16, height: 16, borderRadius: '50%', backgroundColor: workColorById[slot.trabalhoId] || '#7b1fa2', flexShrink: 0 }} />
                                                    )}
                                                    <Autocomplete
                                                        sx={{ flex: 1 }}
                                                        size="small"
                                                        options={selectableTrabalhos}
                                                        getOptionLabel={(option) => formatTrabalhoLabel(option)}
                                                        value={selectableTrabalhos.find(t => t.id === slot.trabalhoId) || null}
                                                        onChange={(_, newValue) => selectExtraTrabalho(slot.slotId, newValue?.id || null)}
                                                        renderInput={(params) => (
                                                            <TextField {...params} label="Selecionar trabalho" variant="outlined" size="small" />
                                                        )}
                                                        isOptionEqualToValue={(option, value) => option.id === value.id}
                                                        noOptionsText="Nenhum trabalho disponível"
                                                    />
                                                    <IconButton
                                                        size="small"
                                                        color="error"
                                                        onClick={() => removeExtraTrabalho(slot.slotId)}
                                                        sx={{ flexShrink: 0 }}
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </Box>
                                                {extraData && (
                                                    <>
                                                        <LinearProgress
                                                            variant="determinate"
                                                            value={extraData.progress}
                                                            sx={{ mt: 0.5, height: 4, borderRadius: 2 }}
                                                        />
                                                        {extraData.loading && (
                                                            <Typography variant="caption" color="text.secondary">
                                                                Carregando dados... {extraData.progress}%
                                                            </Typography>
                                                        )}
                                                    </>
                                                )}
                                            </Box>
                                        );
                                    })}

                                    <Button
                                        variant="outlined"
                                        size="small"
                                        startIcon={<AddIcon />}
                                        onClick={addExtraSlot}
                                        sx={{ mt: 0.5, width: '100%' }}
                                    >
                                        Adicionar trabalho
                                    </Button>
                                </BasicCard>

                            {trabalhosResumoInfo && (
                                <BasicCard style={{ marginBottom: values.margin[1], }} title="Resumo dos trabalhos" color={'primary'}>
                                    <div style={{ flexWrap: 'wrap', padding: 5, margin: 5, justifyContent: 'space-around', }}>
                                        <TextField label={strings.getString('screen_monitorItem_date_initial')} style={{ margin: 5, }} value={trabalhosResumoInfo.dataInicial} InputProps={{ startAdornment: (<InputAdornment position="start"><DateRangeIcon /></InputAdornment>), }} variant="standard" />
                                        <TextField label={strings.getString('screen_monitorItem_date_final')} style={{ margin: 5 }} value={trabalhosResumoInfo.dataFinal} InputProps={{ startAdornment: (<InputAdornment position="start"><DateRangeIcon /></InputAdornment>), }} variant="standard" />
                                        <TextField label={strings.getString('screen_monitorItem_hours_work')} style={{ margin: 5 }} value={trabalhosResumoInfo.horasTrabalhadas} InputProps={{ startAdornment: (<InputAdornment position="start"><AccessTimeIcon /></InputAdornment>), }} variant="standard" />
                                        <TextField label={strings.getString('screen_monitorItem_distance_traveled')} style={{ margin: 5 }} value={trabalhosResumoInfo.distanciaPercorrida} InputProps={{ startAdornment: (<InputAdornment position="start"><TrendingUpIcon /></InputAdornment>), }} variant="standard" />
                                        <TextField label={strings.getString('info_operator')} style={{ margin: 5 }} value={trabalhosResumoInfo.operador} InputProps={{ startAdornment: (<InputAdornment position="start"><PersonIcon /></InputAdornment>), }} variant="standard" />
                                        <TextField label={strings.getString('unity_talhao')} style={{ margin: 5 }} value={trabalhosResumoInfo.talhao} InputProps={{ startAdornment: (<InputAdornment position="start"><MapIcon /></InputAdornment>), }} variant="standard" />
                                    </div>
                                </BasicCard>
                            )}

                            {selectedWorksCardInfo.map((info) => (
                                <Box key={`work-cards-${info.id}`} sx={{ width: '100%' }}>
                                    <BasicCard
                                        style={{ marginBottom: values.margin[1] }}
                                        title={<WorkCardTitle label={strings.getString('screen_monitorItem_general_data')} workId={info.id} color={info.color} />}
                                        color={'primary'}
                                    >
                                        <div style={{ flexWrap: 'wrap', padding: 5, margin: 5, justifyContent: 'space-around', }}>
                                            <TextField label={strings.getString('screen_monitorItem_date_initial')} style={{ margin: 5, }} value={info.dataInicial} InputProps={{ startAdornment: (<InputAdornment position="start"><DateRangeIcon /></InputAdornment>), }} variant="standard" />
                                            <TextField label={strings.getString('screen_monitorItem_date_final')} style={{ margin: 5 }} value={info.dataFinal} InputProps={{ startAdornment: (<InputAdornment position="start"><DateRangeIcon /></InputAdornment>), }} variant="standard" />
                                            <TextField label={strings.getString('screen_monitorItem_hours_work')} style={{ margin: 5 }} value={info.horasTrabalhadas} InputProps={{ startAdornment: (<InputAdornment position="start"><AccessTimeIcon /></InputAdornment>), }} variant="standard" />
                                            <TextField label={strings.getString('screen_monitorItem_distance_traveled')} style={{ margin: 5 }} value={info.distanciaPercorrida} InputProps={{ startAdornment: (<InputAdornment position="start"><TrendingUpIcon /></InputAdornment>), }} variant="standard" />
                                            <TextField label={strings.getString('info_operator')} style={{ margin: 5 }} value={info.operador} InputProps={{ startAdornment: (<InputAdornment position="start"><PersonIcon /></InputAdornment>), }} variant="standard" />
                                            <TextField label={strings.getString('unity_talhao')} style={{ margin: 5 }} value={info.talhao} InputProps={{ startAdornment: (<InputAdornment position="start"><MapIcon /></InputAdornment>), }} variant="standard" />

                                            <Typography style={{ marginTop: values.margin[1] }} color='primary'>{strings.getString('screen_monitorItem_general_settings')}</Typography>
                                            <TextField label={strings.getString('screen_monitorItem_number_lines')} style={{ margin: 5 }} value={info.linhasPlantadeira} InputProps={{ startAdornment: (<InputAdornment position="start"><ViewHeadlineIcon /></InputAdornment>), }} variant="standard" />
                                            <TextField label={strings.getString('screen_monitorItem_antenna_height')} style={{ margin: 5 }} value={info.antenaAlturaM} InputProps={{ startAdornment: (<InputAdornment position="start"><VerticalAlignTopIcon /></InputAdornment>), }} variant="standard" />
                                            <TextField label={strings.getString('screen_monitorItem_line_spacing')} style={{ margin: 5 }} value={info.especamentoLinhas} InputProps={{ startAdornment: (<InputAdornment position="start"><ExpandIcon /></InputAdornment>), }} variant="standard" />
                                            <TextField label={strings.getString('warn_speed_max_permited')} style={{ margin: 5 }} value={info.velocidadeMaxPermitida} InputProps={{ startAdornment: (<InputAdornment position="start"><ReportIcon /></InputAdornment>), }} variant="standard" />
                                        </div>
                                    </BasicCard>

                                    {info.hasPlantio && (
                                        <BasicCard
                                            style={{ marginBottom: values.margin[1] }}
                                            title={<WorkCardTitle label={strings.getString('screen_monitorItem_planting_data')} workId={info.id} color={info.color} />}
                                            color={'primary'}
                                        >
                                            <div style={{ flexWrap: 'wrap', padding: 5, margin: 5, justifyContent: 'space-around', }}>
                                                <TextField label={strings.getString('unity_area')} style={{ margin: 5 }} value={info.areaPlantada} InputProps={{ startAdornment: (<InputAdornment position="start"><RoomIcon /></InputAdornment>), }} variant="standard" />
                                                <TextField label={strings.getString('screen_monitorItem_seeds_meter')} style={{ margin: 5 }} error={!!info.erroSementeMetro} value={info.sementesMetro} InputProps={{ startAdornment: (<InputAdornment position="start"><ScatterPlotIcon /></InputAdornment>), }} variant="standard" />
                                                <TextField label={strings.getString('screen_monitorItem_seeds_hectare')} style={{ margin: 5 }} value={info.sementesHectare} InputProps={{ startAdornment: (<InputAdornment position="start"><ScatterPlotIcon /></InputAdornment>), }} variant="standard" />
                                                <TextField label={strings.getString('screen_monitorItem_total_population')} style={{ margin: 5 }} value={info.populacaoTotal} InputProps={{ startAdornment: (<InputAdornment position="start"><TextureIcon /></InputAdornment>), }} variant="standard" />
                                                <TextField label={strings.getString('screen_monitorItem_average_population_lines')} style={{ margin: 5 }} value={info.populacaoMedia} InputProps={{ startAdornment: (<InputAdornment position="start"><DragIndicatorIcon /></InputAdornment>), }} variant="standard" />

                                                <Typography style={{ marginTop: values.margin[1] }} color='primary'>{strings.getString('screen_monitorItem_planting_settings')}</Typography>
                                                <TextField label={strings.getString('screen_monitorItem_maximum_seed_rate')} style={{ margin: 5 }} value={info.taxaSementesMetroMax} InputProps={{ startAdornment: (<InputAdornment position="start"><ArrowUpwardIcon /></InputAdornment>), }} variant="standard" />
                                                <TextField label={strings.getString('screen_monitorItem_seed_target_rate')} style={{ margin: 5 }} value={info.taxaSementesMetro} InputProps={{ startAdornment: (<InputAdornment position="start"><DoneIcon /></InputAdornment>), }} variant="standard" />
                                                <TextField label={strings.getString('screen_monitorItem_minimum_seed_rate')} style={{ margin: 5 }} value={info.taxaSementesMetroMin} InputProps={{ startAdornment: (<InputAdornment position="start"><ArrowDownwardIcon /></InputAdornment>), }} variant="standard" />
                                            </div>
                                        </BasicCard>
                                    )}

                                    {info.hasPulv && (
                                        <BasicCard
                                            style={{ display: 'flex', marginBottom: values.margin[1] }}
                                            title={<WorkCardTitle label={strings.getString('screen_monitorItem_spray_data')} workId={info.id} color={info.color} />}
                                            color={'primary'}
                                        >
                                            <div style={{ flexWrap: 'wrap', padding: 5, margin: 5, justifyContent: 'space-around', }}>
                                                <TextField label={strings.getString('unity_area_pulverized')} style={{ margin: 5 }} value={info.areaPulverizada} InputProps={{ startAdornment: (<InputAdornment position="start"><RoomIcon /></InputAdornment>), }} variant="standard" />
                                                <TextField label={strings.getString('unity_target_flow')} style={{ margin: 5 }} value={info.vazaoAlvo} InputProps={{ startAdornment: (<InputAdornment position="start"><BrokenImageIcon /></InputAdornment>), }} variant="standard" />
                                                <TextField label={strings.getString('unity_liters_consumed')} style={{ margin: 5 }} value={info.litrosConsumidos} InputProps={{ startAdornment: (<InputAdornment position="start"><LocalDrinkIcon /></InputAdornment>), }} variant="standard" />

                                                <Typography style={{ marginTop: values.margin[1] }} color='primary'>{strings.getString('screen_monitorItem_spray_settings')}</Typography>
                                                <TextField label={strings.getString('screen_monitorItem_pressure_max')} style={{ margin: 5 }} value={info.pressaoMaxPsi} InputProps={{ startAdornment: (<InputAdornment position="start"><ArrowUpwardIcon /></InputAdornment>), }} variant="standard" />
                                                <TextField label={strings.getString('screen_monitorItem_pressure_min')} style={{ margin: 5 }} value={info.pressaoMinPsi} InputProps={{ startAdornment: (<InputAdornment position="start"><ArrowDownwardIcon /></InputAdornment>), }} variant="standard" />
                                                <TextField label={strings.getString('screen_monitorItem_temperature_max')} style={{ margin: 5 }} value={info.configTemperaturaMax} InputProps={{ startAdornment: (<InputAdornment position="start"><WarningAmberIcon /></InputAdornment>), }} variant="standard" />
                                            </div>
                                        </BasicCard>
                                    )}
                                </Box>
                            ))}
                                <BasicCard style={{ marginBottom: values.margin[1], maxWidth: '100%', minWidth: 0, overflowX: 'hidden' }}>
                                    {(!histCoords && !histLinhas?.length && !histPulv?.length && !histPlantio?.length && !histGps?.length) && (
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Carregando dados dos gráficos...</Typography>
                                    )}
                                    <ReportsGraph data={mergedReportData} printTrabalhosInfo={printTrabalhosInfo} />
                                </BasicCard>
                        </RowContainerItem>

                        <RowContainerItem>
                            <Stack sx={{ width: '100%', maxWidth: '100%', minWidth: 0, height: 'auto' }} spacing={1}>
                                {/*
                                <Typography variant="h6" color="primary" sx={{ alignSelf: 'flex-start' }}>
                                    Deslocamento do trator
                                </Typography>
                                {mergedLinhas.linhas.length === 0 && (
                                    <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center' }}>
                                        {mainVisible && linhas.length === 0 ? 'Carregando pontos do mapa...' : !mainVisible && extraTrabalhos.length === 0 ? 'Nenhum trabalho visível no mapa' : ''}
                                    </Typography>
                                )}
                                <MapPanel
                                    markers={mergedLinhas.markers}
                                    zoomMap={zoomMap}
                                    centerMap={mergedLinhas.center || { lat: -24, lng: -52 }}
                                    linhas={mergedLinhas.linhas}
                                    height={600}
                                    implementWidthMeters={implementWidthMeters}
                                />
                                */}

                                <BasicCard style={{ marginBottom: values.margin[1], maxWidth: '100%', minWidth: 0, overflowX: 'hidden' }}>
                                <div style={{ display: 'flex', flexDirection: 'column', width: '100%', minWidth: 0 }}>
                                <Stack
                                    className="no-print"
                                    direction={{ xs: 'column', sm: 'row' }}
                                    spacing={1}
                                    alignItems={{ xs: 'stretch', sm: 'center' }}
                                    sx={{ mt: 1, width: '100%' }}
                                >
                                    <FormControl fullWidth variant="outlined" sx={{ flex: 1, minWidth: 0, maxWidth: '100%' }}>
                                        <InputLabel id="erro-trabalho-select-label">
                                            <WarningAmberIcon style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                                            {strings.getString('screen_workdetails_erro_select_label')}
                                        </InputLabel>
                                        <Select
                                            labelId="erro-trabalho-select-label"
                                            id="erro-trabalho-select"
                                            value={selectedErroTrabalho}
                                            onChange={(event) => setSelectedErroTrabalho(event.target.value)}
                                            label={strings.getString('screen_workdetails_erro_select_label')}
                                            disabled={!hasErroMapCoords}
                                            MenuProps={{
                                                PaperProps: {
                                                    sx: { maxWidth: 'calc(100vw - 32px)' },
                                                },
                                            }}
                                            sx={{
                                                '& .MuiSelect-select': {
                                                    whiteSpace: 'normal',
                                                    wordBreak: 'break-word',
                                                },
                                            }}
                                        >
                                            <MenuItem value="">
                                                <em>{strings.getString('screen_workdetails_erro_select_none')}</em>
                                            </MenuItem>
                                            {trabalhoErros.map((codigo) => (
                                                <MenuItem
                                                    key={codigo}
                                                    value={codigo}
                                                    sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}
                                                >
                                                    {getErroTrabalhoLabel(codigo)}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                        {hasErroMapCoords && trabalhoErros.length === 0 && (
                                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                                {strings.getString('screen_workdetails_erro_select_empty')}
                                            </Typography>
                                        )}
                                    </FormControl>

                                    <Button
                                        variant="outlined"
                                        startIcon={<PrintIcon />}
                                        onClick={handlePrintErroMap}
                                        disabled={
                                            !selectedErroTrabalho ||
                                            (linhasErroTrabalhoData.linhas.length === 0 &&
                                                linhasErroTrabalhoData.savedPoints.length === 0)
                                        }
                                        sx={{ whiteSpace: 'nowrap', minWidth: { xs: '100%', sm: 140 } }}
                                    >
                                        Imprimir mapa
                                    </Button>
                                </Stack>

                                {selectedErroTrabalho && (
                                    <Box ref={printErroMapRef} sx={{ mt: 1, width: '100%' }}>
                                        {isPrintingErroMap && Array.isArray(printTrabalhosInfo) && printTrabalhosInfo.length > 0 && (
                                            printTrabalhosInfo.map((info) => (
                                                <MapPrintHeader key={info.id ?? info.dataInicial} {...info} />
                                            ))
                                        )}
                                        <Typography
                                            variant="h5"
                                            component="h2"
                                            sx={{ mb: 2, fontWeight: 600, color: isPrintingErroMap ? '#000' : 'text.primary' }}
                                        >
                                            {selectedErroLabel}
                                        </Typography>
                                        {linhasErroTrabalhoData.linhas.length === 0 && linhasErroTrabalhoData.savedPoints.length === 0 ? (
                                            <Typography variant="body2" color="text.secondary" className="no-print">
                                                {strings.getString('screen_workdetails_erro_map_empty')}
                                            </Typography>
                                        ) : (
                                            <>
                                                <MapPanel
                                                    zoomMap={zoomMap}
                                                    centerMap={centerMapErroTrabalho || centerMap || { lat: -24, lng: -52 }}
                                                    linhas={linhasErroTrabalhoData.linhas}
                                                    savedPoints={linhasErroTrabalhoData.savedPoints}
                                                    height={400}
                                                    implementWidthMeters={implementWidthMeters}
                                                />
                                                {linhasErroTrabalhoData.legend?.length > 0 && (
                                                    <Box
                                                        className="map-legend-print"
                                                        sx={{
                                                            mt: 1.5,
                                                            display: 'flex',
                                                            flexWrap: 'wrap',
                                                            gap: 1.5,
                                                            alignItems: 'center',
                                                        }}
                                                    >
                                                        {linhasErroTrabalhoData.legend.map((item) => (
                                                            <Box
                                                                key={item.id ?? item.label}
                                                                sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}
                                                            >
                                                                <Box
                                                                    sx={{
                                                                        width: 14,
                                                                        height: 14,
                                                                        borderRadius: '2px',
                                                                        backgroundColor: item.color,
                                                                        border: '1px solid #999',
                                                                        flexShrink: 0,
                                                                    }}
                                                                />
                                                                <Typography
                                                                    variant="body2"
                                                                    sx={{ color: isPrintingErroMap ? '#000' : 'text.primary' }}
                                                                >
                                                                    {item.label}
                                                                </Typography>
                                                            </Box>
                                                        ))}
                                                    </Box>
                                                )}
                                            </>
                                        )}
                                    </Box>
                                )}
                                </div>
                                </BasicCard>

                                <BasicCard style={{ marginBottom: values.margin[3], }}>
                                    {(!mergedReportData[3]?.histCoords?.length && !mergedReportData[0]?.histLinhas?.length && !mergedReportData[1]?.histPulv?.length && !mergedReportData[2]?.histPlantio?.length && !mergedReportData[4]?.histGps?.length) && (
                                        <Typography variant="body2" color="text.secondary">Carregando gráficos e mapas de relatório...</Typography>
                                    )}
                                    <ReportsMaps
                                        data={mergedReportData}
                                        implementWidthMeters={implementWidthMeters}
                                        lineWidthMeters={mainReportMapConfig.lineWidthMeters}
                                        configuredLineCount={mainReportMapConfig.configuredLineCount}
                                        usaSementesMetroSegmento={mainReportMapConfig.usaSementesMetroSegmento}
                                        printTrabalhosInfo={printTrabalhosInfo}
                                    />
                                </BasicCard>
                            </Stack>
                        </RowContainerItem>
                    </RowContainer>
                )}
                {!trabalho && isLoaded &&
                    <div style={{ width: '100%' , display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <ErrorCard
                            onGoBack={onGoBack}
                            page='works'
                            title='Impossível encontrar o registro'
                            message='Não foi possível encontrar o trabalho, se o erro persistir entre em contato com o nosso suporte'
                        />
                    </div>
                }
            </div>
        </>
    );
}
export default CliWorkdetails;
